<template>
  <div class="ui-page credit-analysis">
    <ui-page-header
      title="Central de Análise de Créditos"
      subtitle="Créditos do caso reunidos por credor para revisão, análise e atualização do QGC."
    >
      <ui-action-menu :disabled="!selectedClientId" :items="[
        { text: 'Auditoria do caso', icon: 'mdi-shield-search-outline', handler: openCaseAudit },
        { text: 'Alterar prazo de documentação em lote', icon: 'mdi-calendar-edit', handler: openDeadlineDialog },
        { text: 'Baixar documentos da análise de ofício (.zip)', icon: 'mdi-folder-zip-outline', handler: () => openArchiveDialog() },
        { text: 'Exportar visão', icon: 'mdi-download-outline', disabled: !groups.length, handler: exportView },
      ]" />
      <v-btn outlined color="primary" :disabled="!selectedClientId" @click="load">
        <v-icon left small>mdi-refresh</v-icon>
        Atualizar
      </v-btn>
    </ui-page-header>

    <!-- Tela inicial: um card por caso, no lugar do seletor de caso -->
    <credit-analysis-case-board v-if="!selectedClientId" :cases="cases" :loading="loadingCases" @open="openCase" />

    <ui-section v-else :title="selectedClientName || 'Caso'" class="mb-4">
      <template #actions>
        <v-btn outlined small @click="backToCases">
          <v-icon left small>mdi-arrow-left</v-icon>
          Todos os casos
        </v-btn>
      </template>
      <!-- Painel do caso: prazo de hab/div, pedidos, documentos solicitados e analise de oficio -->
      <credit-analysis-case-panel v-if="selectedCaseSummary" :summary="selectedCaseSummary" />
      <v-row align="center">
        <!-- Desativado: o caso agora e escolhido pelos cards da tela inicial.
        <v-col cols="12" lg="6">
          <v-autocomplete
            v-model="selectedClientId"
            :items="clients"
            :loading="loadingClients"
            item-text="name"
            item-value="id"
            label="Caso judicial *"
            outlined
            dense
            hide-details="auto"
            no-data-text="Nenhum caso encontrado"
            @change="onClientChange"
          />
        </v-col>
        -->
        <!-- Desativado: a Central mostra so a etapa atual do QGC (abaixo).
        <v-col cols="12" md="6" lg="3">
          <v-select
            v-model="qgcVersion"
            :items="qgcVersions"
            item-text="label"
            item-value="id"
            label="Etapa principal do QGC"
            outlined
            dense
            hide-details="auto"
            :disabled="!selectedClientId"
            @change="onQgcVersionChange"
          />
        </v-col>
        -->
        <v-col cols="12" md="6" lg="3">
          <div class="work-version current-stage">
            <span>ETAPA ATUAL DO QGC</span>
            <strong>
              {{ currentStageInfo.label }}
              <v-tooltip bottom max-width="380">
                <template #activator="{ on }">
                  <v-icon v-on="on" small color="primary" class="ml-1" aria-label="O que é esta etapa">mdi-help-circle-outline</v-icon>
                </template>
                <span>{{ currentStageInfo.help }}</span>
              </v-tooltip>
            </strong>
            <small>{{ currentStageInfo.hint }}</small>
          </div>
        </v-col>
        <v-col cols="12" md="6" lg="3">
          <div class="work-version">
            <span>VERSÃO DE TRABALHO</span>
            <strong>Próxima versão</strong>
            <small>rascunho não iniciado</small>
          </div>
        </v-col>
      </v-row>
      <!-- Desativado: a explicacao da fase esta no "?" da etapa atual.
      <div v-if="selectedClientId" class="context-hint mt-3">
        <v-icon small color="primary" class="mr-1">mdi-information-outline</v-icon>
        Art. 52 é a lista inicial da Recuperanda; Art. 7 contém o resultado das Hab/Div; e o
        Consolidado incorpora impugnações judiciais, análises de ofício e demais alterações posteriores.
      </div>
      -->
    </ui-section>

    <!-- Pagina do caso: lista de creditos e cadastro da verificacao (prazos, responsaveis, finalizacao) -->
    <v-tabs v-if="selectedClientId" v-model="caseTab" background-color="transparent" class="case-tabs mb-4">
      <v-tab>Créditos</v-tab>
      <v-tab>Cadastro da verificação</v-tab>
    </v-tabs>
    <credit-analysis-verification-form v-if="selectedClientId && caseTab === 1" :client-id="selectedClientId" :admins="admins" @changed="loadCases" />

    <template v-if="selectedClientId && caseTab === 0">
      <v-alert v-if="isReadOnly" type="warning" text class="read-only-banner">
        <strong>Modo somente leitura — {{ qgcVersionLabel }}</strong>
        Análises, ações em massa e alterações no QGC ficam bloqueadas durante a consulta histórica.
        <v-btn text small color="warning" @click="returnToActiveVersion">Voltar ao QGC atual</v-btn>
      </v-alert>

      <v-row dense>
        <v-col
          v-for="item in primarySummary"
          :key="item.key"
          cols="12"
          sm="6"
          lg="2"
        >
          <v-card
            flat
            class="summary-card"
            :class="{ active: isQuickFilterActive(item) }"
            @click="applySummaryFilter(item)"
          >
            <v-card-text>
              <span class="summary-label">{{ item.label }}</span>
              <strong :class="item.color + '--text'">{{ item.value }}</strong>
              <small>{{ item.hint }}</small>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <v-row dense class="mb-2">
        <v-col
          v-for="item in secondarySummary"
          :key="item.key"
          cols="6"
          sm="4"
          md="2"
        >
          <button type="button" class="secondary-summary" @click="applySummaryFilter(item)">
            <strong>{{ item.value }}</strong>
            <span>{{ item.label }}</span>
          </button>
        </v-col>
      </v-row>

      <v-card flat class="review-progress-card mb-4">
        <div class="review-progress-copy">
          <span>Progresso da revisão</span>
          <strong>{{ summary.concluded || 0 }} de {{ summary.review_total || 0 }} créditos concluídos</strong>
          <small>
            {{ summary.remaining || 0 }} crédito{{ Number(summary.remaining) === 1 ? "" : "s" }}
            ainda {{ Number(summary.remaining) === 1 ? "precisa" : "precisam" }} de conclusão
          </small>
        </div>
        <div class="review-progress-stats">
          <div>
            <strong class="success--text">{{ summary.concluded || 0 }}</strong>
            <span>Concluídos</span>
          </div>
          <div>
            <strong class="warning--text">{{ summary.remaining || 0 }}</strong>
            <span>Faltantes</span>
          </div>
          <div>
            <strong>{{ formatPercentage(summary.completion_percentage) }}</strong>
            <span>Concluído</span>
          </div>
          <div class="review-progress-creditors">
            <strong class="success--text">{{ summary.creditors_concluded || 0 }}<small> / {{ summary.creditors_total || 0 }}</small></strong>
            <span>Credores concluídos</span>
          </div>
          <div>
            <strong class="warning--text">{{ summary.creditors_pending || 0 }}</strong>
            <span>Credores pendentes</span>
          </div>
        </div>
        <v-progress-linear
          :value="summary.completion_percentage || 0"
          height="10"
          rounded
          color="success"
          background-color="grey lighten-3"
        />
      </v-card>

      <ui-table-card :more-count="moreFiltersCount" clearable :can-clear="hasFilters" @clear="clearFilters">
        <template #toolbar>
          <v-text-field
            v-model="filters.name"
            placeholder="Nome do credor"
            prepend-inner-icon="mdi-magnify"
            dense
            outlined
            clearable
            hide-details
            class="ui-toolbar__search"
            @input="filterDebounced"
            @keyup.enter="applyFilters"
          />
          <v-text-field
            v-model="filters.registration"
            placeholder="CPF/CNPJ"
            dense
            outlined
            clearable
            hide-details
            class="ui-toolbar__field"
            @input="filterDebounced"
            @keyup.enter="applyFilters"
          />
          <v-text-field
            v-model="filters.company"
            placeholder="Empresa devedora"
            dense
            outlined
            clearable
            hide-details
            class="ui-toolbar__field"
            @input="filterDebounced"
            @keyup.enter="applyFilters"
          />
          <v-select
            v-model="filters.status"
            :items="statusOptions"
            placeholder="Todos os status"
            dense
            outlined
            clearable
            hide-details
            class="ui-toolbar__field"
            :menu-props="{ offsetY: true }"
            @change="applyFilters"
          />
          <!-- Tipo: com/sem pedido de hab/div, habilitacao ou divergencia -->
          <v-select
            v-model="filters.request_type"
            :items="requestTypeOptions"
            label="Tipo"
            dense
            outlined
            clearable
            hide-details
            class="ui-toolbar__field filter-wide"
            :menu-props="{ offsetY: true }"
            @change="onRequestTypeChange"
          />
          <v-select
            v-model="filters.contradictory"
            :items="contradictoryOptions"
            label="Contraditório"
            dense
            outlined
            clearable
            hide-details
            class="ui-toolbar__field"
            :menu-props="{ offsetY: true }"
            @change="applyFilters"
          />

        </template>
        <!-- Filtros menos usados, abertos em "Mais filtros" -->
        <template #more>
            <v-text-field
              v-model="filters.reference"
              placeholder="Contrato / título / referência"
              dense
              outlined
              clearable
              hide-details
              class="ui-toolbar__field"
              @input="filterDebounced"
              @keyup.enter="applyFilters"
            />
            <v-select
              v-model="filters.type"
              :items="creditorTypes"
              item-text="name"
              item-value="id"
              placeholder="Todas as classes"
              dense
              outlined
              clearable
              hide-details
              class="ui-toolbar__field"
              :menu-props="{ offsetY: true }"
              @change="applyFilters"
            />
            <v-select
              v-model="filters.currency"
              :items="currencies"
              item-text="name"
              item-value="id"
              placeholder="Todas as moedas"
              dense
              outlined
              clearable
              hide-details
              class="ui-toolbar__field"
              :menu-props="{ offsetY: true }"
              @change="applyFilters"
            />
            <v-select
              v-model="filters.source"
              :items="sourceOptions"
              placeholder="Todas as origens"
              dense
              outlined
              clearable
              hide-details
              class="ui-toolbar__field"
              :menu-props="{ offsetY: true }"
              @change="applyFilters"
            />
            <v-select
              v-model="filters.responsible_id"
              :items="responsibleOptions"
              item-text="name"
              item-value="id"
              placeholder="Todos os responsáveis"
              dense
              outlined
              clearable
              hide-details
              class="ui-toolbar__field"
              :menu-props="{ offsetY: true }"
              @change="applyFilters"
            />
            <div class="toolbar-break"></div>
            <v-select
              v-model="filters.creditor_analysis"
              :items="creditorAnalysisOptions"
              label="Análise do credor"
              dense
              outlined
              clearable
              hide-details
              class="ui-toolbar__field"
              :menu-props="{ offsetY: true }"
              @change="applyFilters"
            />
            <div class="toolbar-break"></div>
            <v-checkbox v-model="filters.has_conflict" label="Possui conflito" dense hide-details @change="applyFilters" />
            <v-checkbox v-model="filters.possible_duplicate" label="Possível duplicidade" dense hide-details @change="applyFilters" />
            <v-checkbox v-model="filters.is_overdue" label="Prazo vencido" dense hide-details @change="applyFilters" />
            <v-checkbox v-model="filters.is_due_soon" label="Vencendo em breve" dense hide-details @change="applyFilters" />
            <v-checkbox v-model="filters.grouping_unconfirmed" label="Agrupamento não confirmado" dense hide-details @change="applyFilters" />
            <v-checkbox v-model="filters.official_letter_pending" label="Análise de ofício pendente" dense hide-details @change="applyFilters" />
            <v-checkbox v-model="filters.has_documents" label="Com documentos" dense hide-details @change="applyFilters" />
        </template>

        <div class="quick-filter-bar">
          <span>FILTROS RÁPIDOS</span>
          <v-chip
            v-for="item in quickFilters"
            :key="item.key"
            small
            filter
            outlined
            :color="item.color"
            :input-value="isQuickFilterActive(item)"
            @click="applySummaryFilter(item)"
          >
            {{ item.label }}
            <strong class="ml-1">{{ item.value }}</strong>
          </v-chip>
        </div>
        <div class="analysis-toolbar">
          <div class="selection-block">
            <span v-if="selectedGroups.length">
              {{ selectedCreditCount }} crédito(s) selecionado(s)
            </span>
            <span v-else>Selecione credores para ações em massa</span>
            <v-menu offset-y :disabled="!selectedGroups.length">
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  small
                  outlined
                  color="primary"
                  :disabled="!selectedGroups.length"
                  v-bind="attrs"
                  v-on="on"
                >
                  Ações
                  <v-icon right small>mdi-chevron-down</v-icon>
                </v-btn>
              </template>
              <v-list dense>
                <v-list-item
                  :disabled="!selectedCredits.length || isReadOnly"
                  @click="assignDialog = true"
                >
                  <v-list-item-icon class="mr-2">
                    <v-icon small>mdi-account-arrow-right-outline</v-icon>
                  </v-list-item-icon>
                  <v-list-item-title>Atribuir responsável</v-list-item-title>
                </v-list-item>

                <v-list-item
                  :disabled="!selectedGroups.length || isReadOnly"
                  @click="openBatchOfficialLetter"
                >
                  <v-list-item-icon class="mr-2">
                    <v-icon small>mdi-file-document-edit-outline</v-icon>
                  </v-list-item-icon>
                  <v-list-item-title>Análise de ofício em lote</v-list-item-title>
                </v-list-item>

                <v-list-item
                  :disabled="!selectedCredits.length || isReadOnly"
                  @click="openBatchDecision"
                >
                  <v-list-item-icon class="mr-2">
                    <v-icon small>mdi-clipboard-check-outline</v-icon>
                  </v-list-item-icon>
                  <v-list-item-title>Analisar créditos</v-list-item-title>
                </v-list-item>

                <v-list-item
                  :disabled="!selectedCredits.length"
                  @click="openDocumentRequestDialog('creditor')"
                >
                  <v-list-item-icon class="mr-2">
                    <v-icon small>mdi-file-account-outline</v-icon>
                  </v-list-item-icon>
                  <v-list-item-title>Solicitar documentos ao credor</v-list-item-title>
                </v-list-item>

                <v-list-item
                  :disabled="!selectedCredits.length"
                  @click="openDocumentRequestDialog('client')"
                >
                  <v-list-item-icon class="mr-2">
                    <v-icon small>mdi-file-send-outline</v-icon>
                  </v-list-item-icon>
                  <v-list-item-title>Solicitar documentos à devedora</v-list-item-title>
                </v-list-item>

                <v-divider class="my-1" />

                <v-tooltip bottom>
                  <template v-slot:activator="{ on }">
                    <div v-on="on">
                      <v-list-item disabled>
                        <v-list-item-icon class="mr-2">
                          <v-icon small>mdi-filter-variant</v-icon>
                        </v-list-item-icon>
                        <v-list-item-title>Mover para triagem</v-list-item-title>
                      </v-list-item>
                    </div>
                  </template>
                  <span>O status consolidado será habilitado junto da conclusão da análise.</span>
                </v-tooltip>

              </v-list>
            </v-menu>
          </div>
          <div class="table-controls">
            <v-select
              v-model="sortMode"
              :items="sortOptions"
              label="Ordenar por"
              dense
              outlined
              hide-details
              class="sort-select"
              @change="onSortChange"
            />
            <v-btn small text color="primary" @click="toggleExpandAll">
              {{ allExpanded ? "Recolher todos" : "Expandir todos" }}
            </v-btn>
          </div>
        </div>
        <div class="px-4 py-2">
          <v-switch
            v-model="includeQgc"
            label="Incluir créditos do QGC sem pedido"
            :disabled="loading"
            hide-details
            class="mt-0"
            @change="onIncludeQgcChange"
          />
          <small class="ui-muted">
            Créditos sem análise iniciada não entram nas pendências nem no progresso da revisão.
            <span v-if="includeQgc">{{ summary.qgc_only || 0 }} crédito(s) de análise de ofício.</span>
          </small>
          <!-- Desativado: sem seletor de etapa, a lista ja mostra a etapa atual.
          <v-alert
            v-if="includeQgc && effectiveQgcVersion && effectiveQgcVersion !== qgcVersion"
            dense text type="info" class="mt-2 mb-0"
          >
            A etapa selecionada ainda não possui um QGC próprio. Exibindo os créditos de
            {{ qgcVersionShortLabel(effectiveQgcVersion) }}, a versão mais recente disponível.
          </v-alert>
          -->
        </div>
        <!-- Selecao alem da pagina: acoes em lote (ex.: solicitar documentos) sobre todos os credores do filtro -->
        <v-alert
          v-if="selectedGroups.length && paging.total > groups.length"
          dense text type="info" class="mx-4 mt-2 mb-0"
        >
          <span v-if="selectedGroups.length >= paging.total">
            Todos os <strong>{{ paging.total }}</strong> credores do filtro estão selecionados.
          </span>
          <span v-else>
            <strong>{{ selectedGroups.length }}</strong> selecionado(s) nesta página, de {{ paging.total }} no filtro.
          </span>
          <v-btn
            v-if="selectedGroups.length < paging.total"
            small text color="primary" class="ml-2"
            :loading="selectingAll"
            @click="selectAllMatching"
          >
            Selecionar todos os {{ paging.total }} credores do filtro
          </v-btn>
          <v-btn v-else small text color="primary" class="ml-2" @click="selectedGroups = []">
            Limpar seleção
          </v-btn>
        </v-alert>
        <v-data-table
          v-model="selectedGroups"
          :headers="groupHeaders"
          :items="groups"
          :loading="loading"
          :server-items-length="paging.total"
          :options.sync="tableOptions"
          item-key="key"
          show-select
          show-expand
          :expanded.sync="expandedGroups"
          disable-sort
          hide-default-footer
          class="creditor-table"
          loading-text="Consolidando créditos..."
          no-data-text="Nenhum crédito corresponde aos filtros aplicados"
        >
          <template v-slot:[`item.name`]="{ item }">
            <div class="creditor-cell">
              <span class="ui-cell-main">{{ creditorName(item.name) }}</span>
              <span class="ui-cell-sub">
                {{ formatRegistration(item.registration) }}
                <v-tooltip v-if="item.registration_fictitious" bottom>
                  <template #activator="{ on }">
                    <v-icon v-on="on" small color="warning" class="ml-1">mdi-alert-circle</v-icon>
                  </template>
                  <span>CPF/CNPJ fictício: documento desconhecido na importação. Regularize antes de pagamentos.</span>
                </v-tooltip>
              </span>
            </div>
          </template>
          <template v-slot:[`item.credit_count`]="{ item }">
            {{ item.credit_count }} crédito{{ item.credit_count === 1 ? "" : "s" }}
          </template>
          <template v-slot:[`item.classes`]="{ item }">
            <div class="class-chips">
              <ui-badge
                v-for="(classLabel, index) in item.class_labels"
                :key="`${item.key}-class-${index}`"
                :text="classLabel"
                tone="neutral"
                :dot="false"
              />
              <span v-if="!item.class_labels || !item.class_labels.length">Não informada</span>
            </div>
          </template>
          <template v-slot:[`item.request_types`]="{ item }">
            <div class="request-type-chips">
              <ui-badge
                v-for="requestType in item.request_types"
                :key="`${item.key}-${requestType}`"
                :text="requestType"
                :color="requestType === 'Divergência' ? 'warning' : 'primary'"
              />
              <template v-if="!item.request_types || !item.request_types.length">
                <ui-badge v-if="item.credits.every(credit => isOfficialCredit(credit))" text="Análise de ofício" tone="info" />
                <span v-else>—</span>
              </template>
            </div>
          </template>
          <template v-slot:[`item.submission_count`]="{ item }">
            {{ item.submission_count }} envio{{ item.submission_count === 1 ? "" : "s" }}
          </template>
          <template v-slot:[`item.documents`]="{ item }">
            <v-tooltip bottom>
              <template #activator="{ on }">
                <span v-on="on">
                  <ui-badge v-if="groupDocumentsCount(item)" text="Sim" tone="success" />
                  <ui-badge v-else text="Não" tone="danger" />
                </span>
              </template>
              <span v-if="groupDocumentsCount(item)">{{ groupDocumentsCount(item) }} arquivo(s) recebido(s): anexos da hab/div e solicitações de documentos</span>
              <span v-else>Nenhum arquivo recebido sobre este credor</span>
            </v-tooltip>
          </template>
          <template v-slot:[`item.analysis_concluded`]="{ item }">
            <v-tooltip bottom max-width="320">
              <template #activator="{ on }">
                <span v-on="on" class="d-inline-flex">
                  <v-btn v-if="$allowed('credit.conclude')" icon small :loading="savingReviewKey === item.key" @click.stop="toggleCreditorReview(item)">
                    <v-icon :color="item.analysis_concluded ? 'success' : 'grey lighten-1'">
                      {{ item.analysis_concluded ? "mdi-check-circle" : "mdi-checkbox-blank-circle-outline" }}
                    </v-icon>
                  </v-btn>
                </span>
              </template>
              <span>{{ creditorReviewHint(item) }}</span>
            </v-tooltip>
          </template>
          <template v-slot:[`item.alerts`]="{ item }">
            <ui-badge v-if="item.has_conflict" text="Conflito" tone="danger" class="mr-1" />
            <ui-badge v-if="item.possible_duplicate" text="Duplicidade?" tone="warning" />
          </template>

          <template v-slot:expanded-item="{ headers, item }">
            <td :colspan="headers.length" class="pa-0">
              <div class="credits-wrapper">
                <v-simple-table dense class="credits-table">
                  <thead>
                    <tr>
                      <th>Crédito</th>
                      <th>Classe / moeda</th>
                      <th>Tipo</th>
                      <th>Referência</th>
                      <th>Origens</th>
                      <th>Credor</th>
                      <th>Recuperanda</th>
                      <th>AJ</th>
                      <th>Edital 52</th>
                      <th>Edital 7</th>
                      <th>Consolidado</th>
                      <th>Status</th>
                      <th>Responsável</th>
                      <th>Prazo</th>
                      <th>S/ mov.</th>
                      <th>Alertas</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="credit in item.credits" :key="credit.key">
                      <td>
                        <div class="credit-main">
                          <strong
                            class="credit-company-name"
                            :title="credit.company_name"
                          >
                            {{ credit.company_name }}
                          </strong>
                          <small>{{ credit.submission_count }} envio(s) relacionado(s)</small>
                        </div>
                      </td>
                      <td>
                        <span>{{ credit.creditor_type_label }}</span>
                        <small class="d-block ui-muted">{{ credit.currency_code }}</small>
                      </td>
                      <td>
                        <ui-badge
                          v-for="requestType in credit.request_types"
                          :key="`${credit.key}-${requestType}`"
                          :text="requestType"
                          :color="requestType === 'Divergência' ? 'warning' : 'primary'"
                          class="mr-1"
                        />
                        <template v-if="!credit.request_types || !credit.request_types.length">
                          <ui-badge v-if="isOfficialCredit(credit)" text="Análise de ofício" tone="info" />
                          <span v-else>—</span>
                        </template>
                      </td>
                      <td>{{ credit.reference || "—" }}</td>
                      <td>
                        <ui-badge
                          v-for="origin in credit.origins"
                          :key="origin"
                          :text="sourceLabel(origin)"
                          :color="sourceColor(origin)"
                          class="mr-1 mb-1"
                        />
                      </td>
                      <td>{{ formatAmount(credit.values.creditor, credit.currency_code) }}</td>
                      <td>{{ formatAmount(credit.values.debtor, credit.currency_code) }}</td>
                      <td>{{ formatAmount(credit.values.aj, credit.currency_code) }}</td>
                      <td>{{ formatAmount(qgcStageAmount(credit, 1), credit.currency_code) }}</td>
                      <td>{{ formatAmount(qgcStageAmount(credit, 2), credit.currency_code) }}</td>
                      <td>{{ formatAmount(qgcStageAmount(credit, 3), credit.currency_code) }}</td>
                      <td>
                        <ui-badge :text="statusLabel(credit.status)" :color="statusColor(credit.status)" />
                        <v-tooltip v-if="credit.has_creditor_document_request" bottom>
                          <template #activator="{ on }">
                            <span class="d-block mt-1" v-on="on">
                              <ui-badge :tone="credit.creditor_documents_pending ? 'warning' : 'success'" :dot="false">
                                <v-icon x-small left>mdi-account-outline</v-icon>
                                Credor: {{ credit.creditor_documents_pending ? `${credit.creditor_documents_pending} doc. pendente(s)` : 'documentos enviados' }}
                              </ui-badge>
                            </span>
                          </template>
                          <span>Solicitação de documentos feita ao credor</span>
                        </v-tooltip>
                        <v-tooltip v-if="credit.has_debtor_document_request" bottom>
                          <template #activator="{ on }">
                            <span class="d-block mt-1" v-on="on">
                              <ui-badge :tone="credit.debtor_documents_pending ? 'warning' : 'success'" :dot="false">
                                <v-icon x-small left>mdi-domain</v-icon>
                                Devedora: {{ credit.debtor_documents_pending ? `${credit.debtor_documents_pending} doc. pendente(s)` : 'documentos enviados' }}
                              </ui-badge>
                            </span>
                          </template>
                          <span>Solicitação de documentos feita à devedora sobre este credor</span>
                        </v-tooltip>
                        <small
                          v-if="credit.analysis_draft && credit.analysis_draft.outcome"
                          class="d-block mt-1 decision-result"
                        >
                          {{ outcomeLabel(credit.analysis_draft.outcome) }}
                        </small>
                      </td>
                      <td>{{ credit.responsible_name || "Sem responsável" }}</td>
                      <td>
                        <span :class="{ 'error--text': credit.is_overdue, 'warning--text': credit.is_due_soon }">
                          {{ deadlineLabel(credit) }}
                        </span>
                      </td>
                      <td :class="{ 'warning--text': credit.days_without_activity > 15 }">
                        {{ credit.days_without_activity === null ? "—" : `${credit.days_without_activity} d` }}
                      </td>
                      <td>
                        <v-tooltip
                          v-for="alert in credit.alerts"
                          :key="alert"
                          bottom
                        >
                          <template v-slot:activator="{ on }">
                            <v-icon v-on="on" small :color="alertColor(alert)" class="mr-1">
                              {{ alertIcon(alert) }}
                            </v-icon>
                          </template>
                          <span>{{ alertLabel(alert) }}</span>
                        </v-tooltip>
                      </td>
                      <td>
                        <v-btn text small color="primary" @click="openCredit(credit)">
                          Detalhes
                          <v-icon right small>mdi-chevron-right</v-icon>
                        </v-btn>
                      </td>
                    </tr>
                  </tbody>
                </v-simple-table>
              </div>
            </td>
          </template>
        </v-data-table>

        <template #footer>
          <ui-pagination
            :total="paging.total"
            :page="tableOptions.page"
            :items-per-page="tableOptions.itemsPerPage"
            :disabled="loading"
            @update:page="onPageChange"
            @update:itemsPerPage="setPageSize"
          />
        </template>
      </ui-table-card>

      <ui-section v-if="pendingIdentification.length" flush class="pending-card mt-4">
        <template #header>
          <h3 class="ui-section-title d-flex align-center">
            <v-icon small color="warning" class="mr-2">mdi-account-alert-outline</v-icon>
            Identificação pendente
            <span class="ui-tab-count ui-tab-count--attention ml-2">{{ pendingIdentification.length }}</span>
          </h3>
          <p class="ui-section-description">
            Estes registros não devem ter a análise concluída antes da correção do CPF/CNPJ.
          </p>
        </template>
        <v-simple-table dense>
          <thead>
            <tr>
              <th>Registro</th>
              <th>Nome informado</th>
              <th>CPF/CNPJ</th>
              <th>Empresa devedora</th>
              <th>Motivo</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="item in pendingIdentification" :key="`${item.source_id}-${item.item_id}`">
              <td>#{{ item.source_id }}</td>
              <td>{{ creditorName(item.name) }}</td>
              <td>{{ item.registration || "Não informado" }}</td>
              <td>{{ item.company_name || "Não informada" }}</td>
              <td class="error--text">{{ item.reason }}</td>
              <td>
                <v-btn text small color="primary" :to="`/divergencias/${item.source_id}`">
                  Corrigir
                </v-btn>
              </td>
            </tr>
          </tbody>
        </v-simple-table>
      </ui-section>
    </template>

    <!-- Desativado: sem caso escolhido, a tela mostra os cards dos casos.
    <div v-else class="ui-card">
      <ui-empty icon="mdi-briefcase-search-outline">
        <h3 class="ui-section-title">Selecione um caso judicial</h3>
        <p class="mb-0 mt-1">A central será carregada após a seleção do caso.</p>
      </ui-empty>
    </div>
    -->

    <v-navigation-drawer
      v-model="drawer"
      fixed
      temporary
      right
      width="760"
      class="credit-drawer ui-sheet"
    >
      <div v-if="selectedCredit" class="ui-sheet__inner">
        <div class="ui-sheet__header drawer-header">
          <div>
            <small>CRÉDITO CONSOLIDADO</small>
            <h2>{{ creditorName(selectedCredit.creditor_name) }}</h2>
            <span>
              {{ formatRegistration(selectedCredit.registration) }}
              <v-tooltip v-if="selectedCredit.registration_fictitious" bottom>
                <template #activator="{ on }">
                  <v-icon v-on="on" small color="warning">mdi-alert-circle</v-icon>
                </template>
                <span>CPF/CNPJ fictício: documento desconhecido na importação. Regularize antes de pagamentos.</span>
              </v-tooltip>
              ·
              {{ selectedCredit.company_name }}
            </span>
          </div>
          <v-btn icon @click="drawer = false"><v-icon>mdi-close</v-icon></v-btn>
        </div>
        <div class="ui-sheet__body drawer-content">
          <div class="d-flex flex-wrap align-center mb-5">
            <ui-badge :text="statusLabel(selectedCredit.status)" :color="statusColor(selectedCredit.status)" class="mr-2" />
            <ui-badge
              v-for="alert in selectedCredit.alerts"
              :key="alert"
              :text="alertLabel(alert)"
              :color="alertColor(alert)"
              class="mr-1"
            />
          </div>

          <v-tabs v-model="drawerTab" grow class="drawer-tabs">
            <v-tab>Visão geral</v-tab>
            <v-tab>Envios e análises</v-tab>
            <v-tab>Hab/Div</v-tab>
            <v-tab>Documentos</v-tab>
            <v-tab>Histórico</v-tab>
            <v-tab>Evolução do QGC</v-tab>
            <v-tab>Análise</v-tab>
          </v-tabs>

          <v-tabs-items v-model="drawerTab">
            <v-tab-item>
              <h3 class="mt-5">Comparação entre as origens</h3>
              <div class="comparison-grid">
                <article v-for="origin in comparisonOrigins" :key="origin.key">
                  <span>{{ origin.label }}</span>
                  <strong>{{ formatAmount(origin.amount, selectedCredit.currency_code) }}</strong>
                  <small>{{ origin.detail }}</small>
                </article>
              </div>
              <dl class="credit-metadata">
                <div><dt>Classe</dt><dd>{{ selectedCredit.creditor_type_label }}</dd></div>
                <div><dt>Moeda</dt><dd>{{ selectedCredit.currency_code }}</dd></div>
                <div><dt>Referência</dt><dd>{{ selectedCredit.reference || "Não informada" }}</dd></div>
                <div><dt>Responsável</dt><dd>{{ selectedCredit.responsible_name || "Sem responsável" }}</dd></div>
                <div><dt>Prazo</dt><dd>{{ deadlineLabel(selectedCredit) }}</dd></div>
                <div><dt>Sem movimentação</dt><dd>{{ selectedCredit.days_without_activity }} dia(s)</dd></div>
              </dl>
            </v-tab-item>

            <v-tab-item>
              <h3 class="mt-5">Envios e análises relacionados</h3>
              <v-simple-table dense>
                <thead>
                  <tr>
                    <th>Origem</th>
                    <th>Nº</th>
                    <th>Tipo</th>
                    <th>Valor</th>
                    <th>Status</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="source in selectedCredit.sources" :key="`${source.kind}-${source.reference_id}-${source.origin}`">
                    <td>{{ sourceLabel(source.origin) }}</td>
                    <td>#{{ source.reference_id }}</td>
                    <td>{{ source.type }}</td>
                    <td>{{ formatAmount(source.amount, selectedCredit.currency_code) }}</td>
                    <td>{{ source.status }}</td>
                    <td class="text-no-wrap">
                      <v-tooltip bottom>
                        <template #activator="{ on }">
                          <v-btn icon small v-on="on" @click="openSource(source)">
                            <v-icon small>mdi-arrow-right-circle-outline</v-icon>
                          </v-btn>
                        </template>
                        <span>Abrir nesta aba</span>
                      </v-tooltip>
                      <v-tooltip bottom>
                        <template #activator="{ on }">
                          <v-btn icon small v-on="on" @click="openSourceNewTab(source)">
                            <v-icon small>mdi-open-in-new</v-icon>
                          </v-btn>
                        </template>
                        <span>Abrir em nova aba</span>
                      </v-tooltip>
                    </td>
                  </tr>
                </tbody>
              </v-simple-table>

            </v-tab-item>

            <!-- Aba Hab/Div: resumo do pedido, negocios, motivo e observacoes, com atalho para o pedido -->
            <v-tab-item>
              <v-alert v-if="!selectedEventRequests.length" text type="info" class="mt-5 mb-0">
                Este crédito não tem pedido de habilitação ou divergência. Ele vem apenas do QGC ou de análise de ofício.
              </v-alert>

              <div v-for="request in selectedEventRequests" :key="`drawer-request-${request.reference_id}`" class="mt-5">
                <div class="d-flex align-center justify-space-between flex-wrap mb-2" style="gap: 8px;">
                  <div>
                    <h3 class="mb-0">{{ request.type }} #{{ request.reference_id }}</h3>
                    <small class="ui-muted">
                      Enviada em {{ request.created_at | defaultDate("DD/MM/YYYY HH:mm") }}
                      · Responsável: {{ request.responsible_name || "sem responsável" }}
                    </small>
                  </div>
                  <div class="d-flex align-center" style="gap: 16px;">
                    <ui-badge :text="request.status" :color="requestStatusColor(request.status_id)" />
                    <v-btn small outlined color="primary" @click="openSourceNewTab(request)">
                      <v-icon left small>mdi-open-in-new</v-icon> Ir para a habilitação/divergência
                    </v-btn>
                  </div>
                </div>

                <dl class="credit-metadata mb-3">
                  <div><dt>Negócios</dt><dd>{{ request.items.length }}</dd></div>
                  <div><dt>Valor pleiteado</dt><dd>{{ formatAmount(request.total_amount, selectedCredit.currency_code) }}</dd></div>
                  <div><dt>Prazo do contraditório</dt><dd>{{ request.contradictory_deadline_at ? $options.filters.defaultDate(request.contradictory_deadline_at, "DD/MM/YYYY", false, true) : "não definido" }}</dd></div>
                  <div><dt>Documentos solicitados</dt><dd>{{ request.requested_doc_at ? $options.filters.defaultDate(request.requested_doc_at, "DD/MM/YYYY HH:mm") : "não" }}</dd></div>
                </dl>

                <v-alert v-if="request.contradictory_amount !== null || request.contradictory_reason" text dense type="warning" class="mb-3">
                  <strong>Manifestação da devedora:</strong>
                  <span v-if="request.contradictory_amount !== null"> valor {{ formatAmount(request.contradictory_amount, selectedCredit.currency_code) }}.</span>
                  <div v-if="request.contradictory_reason" style="white-space: pre-wrap;">{{ request.contradictory_reason }}</div>
                </v-alert>

                <div v-if="request.event_small_reason || request.event_reason" class="mb-3">
                  <div class="drawer-tab-heading"><h3>Motivo registrado</h3></div>
                  <div v-if="request.event_small_reason" class="drawer-reason mb-2" v-html="request.event_small_reason"></div>
                  <div v-if="request.event_reason" class="drawer-reason drawer-reason--long" v-html="request.event_reason"></div>
                </div>

                <div class="drawer-tab-heading">
                  <h3>Negócios</h3>
                  <span class="ui-tab-count">{{ request.items.length }}</span>
                </div>
                <v-card v-for="source in request.items" :key="`drawer-business-${source.item_id}`" outlined class="mb-2">
                  <v-card-text class="py-3">
                    <div class="d-flex align-center justify-space-between flex-wrap mb-1">
                      <strong>Negócio #{{ source.item_id }}</strong>
                      <ui-badge :text="itemStatusLabel(source.item_status)" :color="itemStatusColor(source.item_status)" />
                    </div>
                    <v-row dense>
                      <v-col cols="6"><small class="ui-muted">Origem</small><div>{{ source.business_origin || "—" }}</div></v-col>
                      <v-col cols="6"><small class="ui-muted">Empresa devedora</small><div>{{ source.company_name || "—" }}</div></v-col>
                      <v-col cols="6"><small class="ui-muted">Classe informada</small><div>{{ source.creditor_type_label || "—" }}</div></v-col>
                      <v-col cols="6"><small class="ui-muted">Valor pleiteado</small><div>{{ formatAmount(source.amount, selectedCredit.currency_code) }}</div></v-col>
                      <v-col cols="6" v-if="source.partial_amount"><small class="ui-muted">Valor reconhecido parcial</small><div>{{ formatAmount(source.partial_amount, selectedCredit.currency_code) }}</div></v-col>
                      <v-col cols="12" v-if="source.observation"><small class="ui-muted">Observação do credor</small><div style="white-space: pre-wrap;">{{ source.observation }}</div></v-col>
                      <v-col cols="12" v-if="source.item_reason"><small class="ui-muted">Motivo do negócio</small><div class="drawer-reason" v-html="source.item_reason"></div></v-col>
                    </v-row>
                    <div class="mt-2 d-flex flex-wrap" style="gap: 6px;">
                      <v-chip v-for="document in itemDocuments(source)" :key="document.document_id" x-small outlined :href="document.file" target="_blank">
                        <v-icon left x-small>mdi-file-document-outline</v-icon>{{ document.name }}
                      </v-chip>
                      <small v-if="!itemDocuments(source).length" class="ui-muted">Sem documentos anexados</small>
                    </div>
                  </v-card-text>
                </v-card>
              </div>
            </v-tab-item>

            <v-tab-item>
              <div class="d-flex justify-end mb-2">
                <v-btn small outlined color="primary" @click="openArchiveDialog({ name: selectedCredit.creditor_name, registration: selectedCredit.registration })">
                  <v-icon left small>mdi-folder-zip-outline</v-icon>
                  Baixar documentos da análise de ofício (.zip)
                </v-btn>
              </div>
              <!-- Solicitacoes de documentos (ao credor e a devedora): o que foi recebido e o que falta -->
              <template v-if="selectedCredit.document_requests && selectedCredit.document_requests.length">
                <div class="drawer-tab-heading">
                  <h3>Documentos solicitados</h3>
                  <span class="ui-tab-count">{{ selectedCredit.document_requests.length }}</span>
                </div>
                <v-card v-for="request in selectedCredit.document_requests" :key="`drawer-doc-request-${request.id}`" outlined class="mb-3">
                  <v-card-text class="py-3">
                    <div class="d-flex align-center justify-space-between flex-wrap mb-2" style="gap: 6px;">
                      <div>
                        <ui-badge
                          :text="request.target === 'creditor' ? 'Ao credor' : 'À devedora'"
                          :tone="request.target === 'creditor' ? 'primary' : 'neutral'"
                          class="mr-2"
                        />
                        <strong>#{{ request.id }}</strong>
                        <span class="ui-muted ml-1">prazo {{ request.deadline | defaultDate("DD/MM/YYYY") }}</span>
                      </div>
                      <ui-badge :tone="request.documents.some((document) => !document.file_url) ? 'warning' : 'success'">
                        {{ request.documents.filter((document) => !!document.file_url).length }} de {{ request.documents.length }} recebido(s)
                      </ui-badge>
                    </div>
                    <div v-for="document in request.documents" :key="`drawer-doc-${document.id}`" class="d-flex align-center justify-space-between py-1 drawer-doc-row">
                      <span>
                        <v-icon x-small :color="document.file_url ? 'success' : 'warning'" class="mr-1">
                          {{ document.file_url ? 'mdi-check-circle' : 'mdi-clock-outline' }}
                        </v-icon>
                        {{ document.name }}
                      </span>
                      <span class="text-no-wrap">
                        <small class="ui-muted mr-2">
                          {{ document.files && document.files.length ? `${document.files.length} arquivo(s)` : (document.file_url ? 'Recebido' : 'Falta enviar') }}
                        </small>
                        <v-btn v-for="file in (document.files || [])" :key="`drawer-file-${document.id}-${file.id}`" icon x-small :href="file.url" target="_blank" :title="file.name"><v-icon x-small>mdi-download</v-icon></v-btn>
                        <v-btn v-if="!(document.files && document.files.length) && document.file_url" icon x-small :href="document.file_url" target="_blank"><v-icon x-small>mdi-download</v-icon></v-btn>
                      </span>
                    </div>
                  </v-card-text>
                </v-card>
              </template>

              <div class="drawer-tab-heading">
                <h3>Documentos relacionados</h3>
                <span class="ui-tab-count">{{ selectedCredit.documents.length }}</span>
              </div>
              <div v-if="selectedCredit.documents.length" class="document-list">
                <article v-for="document in selectedCredit.documents" :key="document.document_id">
                  <v-icon color="primary">mdi-file-document-outline</v-icon>
                  <div>
                    <strong>{{ document.name }}</strong>
                    <small>
                      {{ sourceLabel(document.origin) }} · envio #{{ document.source_reference_id }} ·
                      {{ document.created_at | defaultDate("DD/MM/YYYY HH:mm") }}
                    </small>
                  </div>
                  <v-btn icon small :href="document.file" target="_blank">
                    <v-icon small>mdi-open-in-new</v-icon>
                  </v-btn>
                </article>
              </div>
              <v-alert v-else text type="info">Nenhum documento relacionado a este crédito.</v-alert>
            </v-tab-item>

            <v-tab-item>
              <div class="drawer-tab-heading">
                <h3>Histórico do crédito consolidado</h3>
                <span class="ui-tab-count">{{ selectedCredit.history.length }}</span>
              </div>
              <div class="history-list">
                <article v-for="(entry, index) in selectedCredit.history" :key="`${entry.kind}-${entry.reference_id}-${entry.item_id}-${index}`">
                  <span :class="`history-dot ${entry.origin}`"></span>
                  <div>
                    <strong>{{ entry.description }}</strong>
                    <small>
                      {{ sourceLabel(entry.origin) }} · #{{ entry.reference_id }} ·
                      {{ entry.occurred_at | defaultDate("DD/MM/YYYY HH:mm") }}
                    </small>
                  </div>
                </article>
              </div>
            </v-tab-item>

            <v-tab-item>
              <div class="qgc-evolution mt-5">
                <article v-for="stage in qgcStageDefinitions" :key="stage.id">
                  <span>{{ stage.label }}</span>
                  <strong>{{ formatAmount(qgcStageAmount(selectedCredit, stage.id), selectedCredit.currency_code) }}</strong>
                  <small>{{ stage.description }}</small>
                  <small v-if="qgcStageCount(selectedCredit, stage.id)">
                    {{ qgcStageCount(selectedCredit, stage.id) }} crédito(s) correspondente(s)
                  </small>
                  <small v-else>Sem correspondência por CPF/CNPJ, classe e moeda</small>
                </article>
              </div>
            </v-tab-item>

            <v-tab-item>
              <div class="drawer-tab-heading">
                <div>
                  <h3>Preparar análise do crédito</h3>
                  <small>
                    O rascunho não conclui a análise e pode ser revisado antes da finalização.
                  </small>
                </div>
                <ui-badge v-if="selectedCredit.analysis_draft" text="Rascunho salvo" tone="primary" />
              </div>

              <v-alert text type="info" dense class="mb-4">
                A análise será aplicada aos {{ selectedCredit.sources.filter(source => source.kind === "user_event").length }}
                envio(s) relacionado(s) quando a análise for concluída.
              </v-alert>

              <v-form class="drawer-decision-form" @submit.prevent="saveDrawerDraft">
                <v-select
                  v-model="drawerDecision.outcome"
                  :items="outcomeOptions"
                  label="Status proposto"
                  outlined
                  dense
                  clearable
                  hint="Aprovar, rejeitar ou aprovar parcialmente"
                  persistent-hint
                  @change="applyDrawerDecisionOutcome"
                />

                <v-text-field
                  v-model.number="drawerDecision.recognized_amount"
                  type="number"
                  label="Novo valor reconhecido"
                  prefix="R$"
                  outlined
                  dense
                  :disabled="drawerDecision.outcome === 'rejected'"
                  :error="drawerDecision.outcome === 'partial' && !isDrawerPartialAmountValid"
                  :hint="drawerDecision.outcome === 'partial' ? 'Obrigatório para aprovação parcial' : 'Valor que será considerado na análise'"
                  persistent-hint
                />

                <v-select
                  v-model="drawerDecision.creditor_type"
                  :items="creditorTypes"
                  item-text="name"
                  item-value="id"
                  label="Nova classe"
                  outlined
                  dense
                />

                <v-textarea
                  v-model="drawerDecision.reason"
                  label="Motivo / fundamentação"
                  outlined
                  rows="4"
                  counter="5000"
                  hint="Registre os fundamentos que serão revisados na conclusão"
                  persistent-hint
                />

                <div class="drawer-draft-actions">
                  <v-btn v-if="$allowed('save')"
                    type="submit"
                    color="primary"
                    :loading="savingDrawerDraft"
                    :disabled="isReadOnly || !canSaveDrawerDraft"
                  >
                    <v-icon left small>mdi-content-save-outline</v-icon>
                    Salvar rascunho
                  </v-btn>
                  <v-btn text color="primary" @click="openFullAnalysis">
                    Abrir revisão completa
                  </v-btn>
                </div>
              </v-form>
            </v-tab-item>
          </v-tabs-items>

          <v-alert
            v-if="selectedCredit.grouping_status === 'suggested'"
            type="info"
            text
            class="mt-6"
          >
            Este agrupamento foi sugerido por CPF/CNPJ, empresa devedora, classe e moeda.
            A confirmação e separação manual serão habilitadas na próxima etapa.
          </v-alert>
        </div>
        <div class="ui-sheet__footer drawer-actions">
          <!-- Desativado: a analise de oficio e aberta em lote (solicitacao de documentos ou Acoes).
          <v-btn v-if="$allowed('create')"
            outlined
            small
            color="primary"
            :disabled="isReadOnly"
            @click="openOfficialLetterAnalysis"
          >
            <v-icon left small>mdi-file-document-edit-outline</v-icon>
            Incluir análise de ofício
          </v-btn>
          -->
          <v-btn v-if="$allowed('credit.assign')"
            outlined
            small
            color="primary"
            :loading="assigning"
            :disabled="isReadOnly || !selectedCredit"
            @click="assignToMe"
          >
            Atribuir a mim
          </v-btn>
          <v-tooltip bottom>
            <template v-slot:activator="{ on }">
              <span v-on="on">
                <v-btn
                  outlined small color="primary"
                  :disabled="isReadOnly || !selectedCreditRequestTargets.length"
                  @click="openDocumentRequest"
                >
                  Solicitar documentos
                </v-btn>
              </span>
            </template>
            <span v-if="selectedCreditRequestTargets.length">
              Escolha o pedido relacionado e os documentos que devem ser enviados pelo credor.
            </span>
            <span v-else>
              Este crédito não possui pedido de Hab/Div relacionado para receber a solicitação.
            </span>
          </v-tooltip>
          <v-tooltip bottom>
            <template v-slot:activator="{ on }">
              <span v-on="on"><v-btn v-if="$allowed('edit')" outlined small color="primary" disabled>Confirmar agrupamento</v-btn></span>
            </template>
            <span>A confirmação será persistida na etapa de conclusão do crédito.</span>
          </v-tooltip>
          <v-btn color="primary" small :disabled="isReadOnly" @click="openFullAnalysis">
            Revisar e concluir análise
            <v-icon right small>mdi-arrow-right</v-icon>
          </v-btn>
        </div>
      </div>
    </v-navigation-drawer>

    <v-dialog v-model="assignDialog" max-width="480">
      <div class="ui-card">
        <header class="ui-card__header">
          <h3 class="ui-section-title">Atribuir responsável</h3>
        </header>
        <div class="ui-card__body">
          <p class="ui-muted">
            {{ selectedCredits.length }} crédito(s) terão um responsável pela análise na Central, independentemente da origem.
          </p>
          <v-select
            v-model="selectedResponsibleId"
            :items="admins"
            item-text="name"
            item-value="id"
            label="Responsável"
            outlined
            dense
          />
        </div>
        <footer class="ui-card__footer">
          <v-btn text @click="assignDialog = false">Cancelar</v-btn>
          <v-btn v-if="$allowed('credit.assign')"
            color="primary"
            depressed
            :disabled="!selectedResponsibleId"
            :loading="assigning"
            @click="assignSelectedResponsible"
          >
            Atribuir
          </v-btn>
        </footer>
      </div>
    </v-dialog>

    <document-request-deadline-dialog v-model="deadlineDialog" :client-id="selectedClientId" @saved="load" />

    <credit-analysis-archive-dialog v-model="archiveDialog" :client-id="selectedClientId" :creditor="archiveCreditor" />

    <debtor-document-request-dialog
      v-model="debtorRequestDialog"
      :client-id="selectedClientId"
      :credits="selectedCredits"
      :default-target="documentRequestTarget"
      :habdiv-deadline="habdivDeadline"
      @sent="onDebtorRequestSent"
    />

    <v-dialog v-model="documentRequest.dialog" max-width="720" persistent scrollable>
      <v-card>
        <v-card-title>Solicitar documentos</v-card-title>
        <v-card-text>
          <p v-if="selectedCredit" class="mb-4">
            Crédito de <strong>{{ selectedCredit.creditor_name }}</strong>
          </p>
          <v-select
            v-model="documentRequest.target"
            :items="selectedCreditRequestTargets"
            item-text="label"
            return-object
            label="Pedido de Hab/Div destinatário"
            outlined dense
            :disabled="selectedCreditRequestTargets.length === 1"
          />
          <v-alert dense text type="info">
            A solicitação será enviada pelo fluxo do pedido selecionado e alterará seu status para
            “Documentos solicitados”. E-mail: <strong>{{ selectedCredit && selectedCredit.creditor_email || 'não informado' }}</strong>.
          </v-alert>

          <div class="font-weight-medium mb-2">Documentos gerais</div>
          <v-checkbox
            v-for="doc in generalRequestDocumentTypes"
            :key="`general-${doc.id}`"
            v-model="documentRequest.generalDocs"
            :label="doc.name"
            :value="doc.id"
            hide-details class="mt-1"
          />

          <div class="font-weight-medium mt-5 mb-2">Documentos comprobatórios do crédito</div>
          <v-checkbox
            v-for="doc in businessDocumentTypes"
            :key="`business-${doc.id}`"
            v-model="documentRequest.businessDocs"
            :label="doc.name"
            :value="doc.id"
            hide-details class="mt-1"
          />
          <v-text-field
            v-if="documentRequest.businessDocs.includes(50)"
            v-model="documentRequest.customName"
            label="Nome do outro documento"
            outlined dense class="mt-3"
          />
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text :disabled="documentRequest.saving" @click="closeDocumentRequest">Cancelar</v-btn>
          <v-btn
            color="primary"
            :loading="documentRequest.saving"
            :disabled="!canSendDocumentRequest"
            @click="sendDocumentRequest"
          >
            Solicitar documentos
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Desativado: analise de oficio individual (inclui ou edita a existente).
    <v-dialog v-model="officialLetter.dialog" max-width="640" persistent>
      <div class="ui-card">
        <header class="ui-card__header">
          <h3 class="ui-section-title">
            {{ officialLetter.id ? 'Editar análise de ofício' : 'Incluir análise de ofício' }}
          </h3>
        </header>
        <div class="ui-card__body">
          <div class="mb-3">
            <div class="ui-label">Credor</div>
            <div class="font-weight-medium">{{ officialLetter.creditorName }}</div>
          </div>

          <v-text-field
            v-model.number="officialLetter.amount"
            label="Valor *"
            type="number"
            min="0"
            step="0.01"
            outlined
            dense
          />

          <v-textarea
            v-model="officialLetter.reason"
            label="Análise (opcional)"
            rows="5"
            outlined
            dense
            hide-details="auto"
          />
          <p class="ui-muted ui-small mt-2 mb-0">
            Sem a análise preenchida, o crédito fica sinalizado como análise pendente na listagem.
          </p>
        </div>
        <footer class="ui-card__footer">
          <v-btn text :disabled="officialLetter.saving" @click="officialLetter.dialog = false">
            Cancelar
          </v-btn>
          <v-btn v-if="$allowed('save')"
            color="primary"
            depressed
            :loading="officialLetter.saving"
            @click="saveOfficialLetter"
          >
            Salvar
          </v-btn>
        </footer>
      </div>
    </v-dialog>
    -->

    <!-- Análise de ofício em lote -->
    <v-dialog v-model="batchOfficialLetter.dialog" max-width="1100" persistent scrollable>
      <v-card>
        <v-card-title class="batch-letter-title">
          <div>
            <span>Análise de ofício em lote</span>
            <small>Revise classe, moeda e valor antes de criar as análises</small>
          </div>
          <v-btn icon @click="batchOfficialLetter.dialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-divider />

        <v-card-text class="batch-letter-body">
          <section class="batch-letter-section">
            <header>
              <v-icon small>mdi-text-box-outline</v-icon>
              <span>Análise</span>
              <em>opcional</em>
            </header>
            <v-textarea
              v-model="batchOfficialLetter.reason"
              placeholder="Descreva a análise que será aplicada a todos os créditos abaixo"
              rows="4"
              outlined
              dense
              hide-details="auto"
            />
            <p class="batch-letter-hint">
              Em branco, os ofícios são criados e os créditos ficam sinalizados
              como <strong>análise pendente</strong> na listagem.
            </p>
          </section>

          <section class="batch-letter-section">
            <header>
              <v-icon small>mdi-format-list-bulleted</v-icon>
              <span>Créditos</span>
              <em>{{ batchOfficialLetter.items.length }}</em>
              <v-spacer />
              <v-btn v-if="$allowed('create')" small outlined color="primary" @click="addBatchCreditorRow">
                <v-icon left small>mdi-account-plus-outline</v-icon>
                Adicionar credor
              </v-btn>
            </header>

            <div v-if="batchOfficialLetter.items.length" class="batch-letter-rows">
              <article
                v-for="(item, index) in batchOfficialLetter.items"
                :key="item.rowId"
                :class="['batch-letter-row', { 'batch-letter-row--new': item.isNew }]"
              >
                <div class="batch-letter-row__head">
                  <ui-badge :text="item.isNew ? 'Novo credor' : 'Do quadro'" :tone="item.isNew ? 'primary' : 'neutral'" />
                  <v-spacer />
                  <v-tooltip bottom>
                    <template v-slot:activator="{ on }">
                      <v-btn icon x-small v-on="on" @click="addBatchCreditRow(item)">
                        <v-icon small>mdi-plus-circle-outline</v-icon>
                      </v-btn>
                    </template>
                    <span>Adicionar outro crédito para este credor</span>
                  </v-tooltip>
                  <v-tooltip bottom>
                    <template v-slot:activator="{ on }">
                      <v-btn icon x-small v-on="on" @click="removeBatchRow(index)">
                        <v-icon small color="error">mdi-trash-can-outline</v-icon>
                      </v-btn>
                    </template>
                    <span>Remover da análise</span>
                  </v-tooltip>
                </div>

                <div class="batch-letter-row__fields">
                  <v-text-field
                    v-model="item.creditor_name"
                    :readonly="!item.isNew"
                    :filled="!item.isNew"
                    label="Credor"
                    dense outlined hide-details
                    placeholder="Nome do credor"
                  />
                  <v-text-field
                    v-model="item.registrationMasked"
                    :readonly="!item.isNew"
                    :filled="!item.isNew"
                    label="CPF/CNPJ"
                    dense outlined hide-details
                    placeholder="000.000.000-00"
                    maxlength="18"
                  />
                  <v-select
                    v-model="item.type"
                    :items="creditorTypes"
                    item-text="name"
                    item-value="id"
                    label="Classe"
                    dense outlined hide-details
                  />
                  <v-select
                    v-model="item.currency"
                    :items="currencies"
                    item-text="name"
                    item-value="id"
                    label="Moeda"
                    dense outlined hide-details
                  />
                  <v-currency-field
                    v-model="item.amount"
                    label="Valor"
                    :allow-negative="false"
                    dense outlined hide-details
                  />
                </div>
              </article>
            </div>

            <div v-else class="batch-letter-empty">
              <v-icon>mdi-file-document-outline</v-icon>
              <p>Nenhum crédito na análise</p>
              <span>Use "Adicionar credor" para incluir um.</span>
            </div>
          </section>
        </v-card-text>

        <v-divider />

        <v-card-actions class="batch-letter-actions">
          <span class="batch-letter-summary">
            {{ batchOfficialLetter.items.length }} análise(s) serão criadas
          </span>
          <v-spacer />
          <v-btn text :disabled="batchOfficialLetter.saving" @click="batchOfficialLetter.dialog = false">
            Cancelar
          </v-btn>
          <v-btn v-if="$allowed('letter.create')"
            color="primary"
            elevation="0"
            :disabled="!batchOfficialLetter.items.length"
            :loading="batchOfficialLetter.saving"
            @click="saveBatchOfficialLetter"
          >
            <v-icon left small>mdi-check</v-icon>
            Criar análises
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="decisionDialog" max-width="1100" scrollable>
      <v-card>
        <v-card-title>Análise conjunta dos créditos selecionados</v-card-title>
        <v-card-subtitle>
          Os pedidos do Credor, Recuperanda e AJ permanecem reunidos por crédito.
          Defina uma análise individual para cada crédito abaixo.
        </v-card-subtitle>
        <v-card-text>
          <v-alert v-if="decisionConflict" type="error" text>
            <strong>{{ decisionConflict.message }}</strong>
            <ul class="mt-2 mb-0">
              <li v-for="blocker in decisionConflict.blockers" :key="blocker.credit_key">
                {{ creditorName(blocker.creditor_name) }} · {{ blocker.company_name }} ·
                {{ blocker.reference || "sem referência" }}
              </li>
            </ul>
            <template v-if="canOverrideDuplicates">
              <v-checkbox
                v-model="duplicateOverride"
                label="Autorizar conclusão excepcional destes créditos"
                hide-details
                class="mt-3"
              />
              <v-textarea
                v-if="duplicateOverride"
                v-model="duplicateJustification"
                label="Justificativa obrigatória da exceção"
                outlined
                rows="3"
                class="mt-3"
              />
            </template>
            <small v-else class="d-block mt-3">
              Solicite a um administrador com perfil Geral que revise ou autorize a exceção.
            </small>
          </v-alert>
          <v-simple-table>
            <thead>
              <tr>
                <th>Credor</th>
                <th>Empresa devedora</th>
                <th>Referência</th>
                <th>Origens</th>
                <th>Resultado</th>
                <th>Valor reconhecido</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="row in decisionRows" :key="row.credit.key">
                <td>
                  <strong>{{ creditorName(row.credit.creditor_name) }}</strong>
                </td>
                <td class="decision-company-field">
                  <v-select
                    v-if="clientCompanies.length"
                    v-model="row.client_company_id"
                    :items="clientCompanies"
                    item-text="name"
                    item-value="id"
                    dense
                    outlined
                    hide-details="auto"
                    placeholder="Selecione"
                    :error="!row.client_company_id"
                    @change="applyDecisionCompany(row)"
                  />
                  <span v-else>{{ row.company_name || row.credit.company_name }}</span>
                </td>
                <td>{{ row.credit.reference || "—" }}</td>
                <td>
                  <ui-badge
                    v-for="origin in row.credit.origins"
                    :key="origin"
                    :text="sourceLabel(origin)"
                    :color="sourceColor(origin)"
                    class="mr-1"
                  />
                </td>
                <td class="decision-field">
                  <v-select
                    v-model="row.outcome"
                    :items="outcomeOptions"
                    dense
                    outlined
                    hide-details="auto"
                    @change="applyDecisionOutcome(row)"
                  />
                </td>
                <td class="decision-field">
                  <v-text-field
                    v-model.number="row.recognized_amount"
                    type="number"
                    dense
                    outlined
                    hide-details="auto"
                    :disabled="row.outcome === 'rejected'"
                    :error="row.outcome === 'partial' && !isPartialAmountValid(row)"
                  />
                </td>
              </tr>
            </tbody>
          </v-simple-table>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="decisionDialog = false">Cancelar</v-btn>
          <v-btn v-if="$allowed('credit.conclude')"
            color="primary"
            :loading="savingDecisions"
            :disabled="!canSaveBatchDecisions"
            @click="saveBatchDecisions"
          >
            Concluir análises
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { downloadXlsx } from "@/common/util/export-xlsx.util";
import axios from "axios";
import _ from "lodash";
import creditorTypes from "@/common/data/creditor-types.data";
import creditorClassUtil from "@/common/util/creditor-class.util";
import currencies from "@/common/data/currency-types.data";
import businessDocumentTypes from "@/common/data/business-doc-types.data";
import DebtorDocumentRequestDialog from "@/components/DebtorDocumentRequestDialog.vue";
import DocumentRequestDeadlineDialog from "@/components/DocumentRequestDeadlineDialog.vue";
import CreditAnalysisArchiveDialog from "@/components/CreditAnalysisArchiveDialog.vue";
import CreditAnalysisCaseBoard from "@/components/CreditAnalysisCaseBoard.vue";
import CreditAnalysisCasePanel from "@/components/CreditAnalysisCasePanel.vue";
import CreditAnalysisVerificationForm from "@/components/CreditAnalysisVerificationForm.vue";

const emptyFilters = () => ({
  name: "",
  registration: "",
  company: "",
  reference: "",
  type: null,
  currency: null,
  status: null,
  request_type: null,
  source: null,
  responsible_id: null,
  has_conflict: false,
  possible_duplicate: false,
  is_overdue: false,
  is_due_soon: false,
  grouping_unconfirmed: false,
  official_letter_pending: false,
  has_documents: false,
  contradictory: null,
  creditor_analysis: null,
});

export default {
  name: "CreditAnalysisList",

  components: { DebtorDocumentRequestDialog, DocumentRequestDeadlineDialog, CreditAnalysisArchiveDialog, CreditAnalysisCaseBoard, CreditAnalysisCasePanel, CreditAnalysisVerificationForm },

  data: () => ({
    breadcrumbs: [
      { text: "Homepage", disabled: false, to: "/home", exact: true },
      { text: "Central de Análise de Créditos", disabled: true },
    ],
    clients: [],
    cases: [],
    loadingCases: false,
    // Aba da pagina do caso: 0 = Creditos, 1 = Cadastro da verificacao.
    caseTab: 0,
    clientCompanies: [],
    habdivDeadline: null,
    archiveDialog: false,
    archiveCreditor: null,
    admins: [],
    selectedClientId: null,
    qgcVersion: 3,
    qgcVersions: [
      { id: 1, label: "Edital Art. 52 — lista inicial" },
      { id: 2, label: "Edital Art. 7 — após Hab/Div" },
      { id: 3, label: "QGC Consolidado — etapa atual" },
    ],
    creditorTypes,
    currencies,
    businessDocumentTypes,
    generalRequestDocumentTypes: [
      { id: 1, name: "Documento de identificação" },
      { id: 2, name: "Procuração" },
      { id: 4, name: "Documento do procurador" },
    ],
    filters: emptyFilters(),
    loading: false,
    loadingClients: false,
    groups: [],
    pendingIdentification: [],
    summary: {},
    paging: { page: 1, perPage: 25, total: 0, lastPage: 1 },
    tableOptions: { page: 1, itemsPerPage: 25 },
    includeQgc: false,
    effectiveQgcVersion: null,
    qgcCurrentStage: null,
    selectedGroups: [],
    selectingAll: false,
    expandedGroups: [],
    sortMode: "priority",
    sortOptions: [
      { text: "Prioridade", value: "priority" },
      { text: "Classe", value: "class" },
      { text: "Prazo mais próximo", value: "deadline" },
      { text: "Análises vencidas", value: "overdue" },
      { text: "Última movimentação", value: "activity" },
      { text: "Status consolidado", value: "status" },
      { text: "Possíveis duplicidades", value: "duplicates" },
    ],
    footerOptions: {
      itemsPerPageText: "Credores por página:",
      itemsPerPageOptions: [10, 25, 50, 100],
      pageText: "{0}-{1} de {2}",
    },
    groupHeaders: [
      { text: "Credor", value: "name" },
      { text: "Classe", value: "classes", sortable: false },
      { text: "Tipo", value: "request_types", sortable: false },
      { text: "Créditos", value: "credit_count", sortable: false },
      { text: "Envios", value: "submission_count", sortable: false },
      { text: "Documentos", value: "documents", sortable: false },
      { text: "Alertas", value: "alerts", sortable: false },
      { text: "Concluída", value: "analysis_concluded", sortable: false, align: "center" },
      { text: "", value: "data-table-expand" },
    ],
    statusOptions: [
      { text: "Análise de ofício (sem pedido)", value: "qgc_only" },
      { text: "Pendentes (não iniciado + triagem)", value: "pending" },
      { text: "Não iniciado", value: "not_started" },
      { text: "Pendente de triagem", value: "triage" },
      { text: "Em análise", value: "analysis" },
      { text: "Aguardando documentos", value: "waiting_documents" },
      { text: "Com conflito", value: "conflict" },
      { text: "Pronto para concluir", value: "ready" },
      { text: "Concluído", value: "concluded" },
    ],
    requestTypeOptions: [
      { text: "Com pedido", value: "with_request" },
      { text: "Habilitação", value: "habilitacao" },
      { text: "Divergência", value: "divergencia" },
      { text: "Sem pedido (ofício)", value: "without_request" },
    ],
    creditorAnalysisOptions: [
      { text: "Análise concluída", value: "concluded" },
      { text: "Análise pendente", value: "pending" },
    ],
    savingReviewKey: null,
    contradictoryOptions: [
      { text: "Aberto", value: "open" },
      { text: "Fechado", value: "closed" },
    ],
    sourceOptions: [
      { text: "Credor", value: "creditor" },
      { text: "Recuperanda", value: "debtor" },
      { text: "AJ (Análise de Ofício)", value: "aj" },
    ],
    drawer: false,
    drawerTab: 0,
    selectedCredit: null,
    savingDrawerDraft: false,
    drawerDecision: {
      outcome: null,
      recognized_amount: null,
      creditor_type: null,
      currency: null,
      client_company_id: null,
      company_name: "",
      reference: "",
      qgc_action: "update",
      related_requests_action: "joint",
      reason: "",
    },
    assignDialog: false,
    debtorRequestDialog: false,
    deadlineDialog: false,
    documentRequestTarget: "creditor",
    documentRequest: {
      dialog: false,
      saving: false,
      target: null,
      generalDocs: [],
      businessDocs: [],
      customName: "",
    },
    // Desativado: dados do modal de analise de oficio individual.
    // officialLetter: {
      // dialog: false,
      // saving: false,
      // id: null,
      // creditKey: null,
      // creditorName: "",
      // creditorPayload: null,
      // amount: null,
      // reason: "",
    // },

    batchOfficialLetter: {
      dialog: false,
      saving: false,
      reason: "",
      rowSeq: 0,
      items: [],
    },
    assigning: false,
    selectedResponsibleId: null,
    decisionDialog: false,
    decisionRows: [],
    savingDecisions: false,
    decisionConflict: null,
    duplicateOverride: false,
    duplicateJustification: "",
    outcomeOptions: [
      { text: "Aprovar", value: "approved" },
      { text: "Rejeitar", value: "rejected" },
      { text: "Aprovar parcialmente", value: "partial" },
    ],
  }),

  computed: {
    // Um registro por negocio do pedido de hab/div do credito aberto no painel.
    selectedEventSources() {
      return (this.selectedCredit && this.selectedCredit.sources || [])
        .filter((source) => source.kind === "user_event" && source.item_id)
        .filter((source, index, list) => list.findIndex((item) => Number(item.item_id) === Number(source.item_id)) === index);
    },

    // Pedidos de hab/div do credito, com os negocios agrupados por pedido.
    selectedEventRequests() {
      const requests = new Map();
      this.selectedEventSources.forEach((source) => {
        const id = Number(source.reference_id);
        if (!requests.has(id)) {
          requests.set(id, {
            kind: "user_event",
            reference_id: id,
            type: source.type,
            status: source.status,
            status_id: source.status_id,
            created_at: source.created_at,
            responsible_name: source.responsible_name,
            event_reason: source.event_reason,
            event_small_reason: source.event_small_reason,
            contradictory_deadline_at: source.contradictory_deadline_at,
            contradictory_amount: source.contradictory_amount === undefined ? null : source.contradictory_amount,
            contradictory_reason: source.contradictory_reason,
            requested_doc_at: source.requested_doc_at,
            total_amount: 0,
            items: [],
          });
        }
        const request = requests.get(id);
        request.items.push(source);
        request.total_amount += Number(source.amount || 0);
      });
      return [...requests.values()];
    },

    responsibleOptions() {
      return [{ id: "unassigned", name: "Sem responsável" }, ...this.admins];
    },

    // Etapa atual do QGC do caso (a mais nova gerada) e a explicacao da fase.
    currentStageInfo() {
      const stage = Number(this.qgcCurrentStage) || null;
      return ({
        1: {
          label: "Edital Art. 52",
          hint: "lista inicial da recuperanda",
          help: "Fase de habilitações e divergências: o QGC é a lista inicial apresentada pela recuperanda (art. 52, § 1º). Os pedidos dos credores e as análises de ofício feitas aqui formam o Edital do art. 7º.",
        },
        2: {
          label: "Edital Art. 7",
          hint: "após as habilitações e divergências",
          help: "Edital do art. 7º, § 2º: resultado da análise das habilitações e divergências. A partir dele correm as impugnações judiciais (art. 8º); essas decisões e as análises de ofício posteriores levam ao QGC consolidado.",
        },
        3: {
          label: "QGC Consolidado",
          hint: "quadro atual",
          help: "QGC consolidado: o Edital do art. 7º com as impugnações julgadas, as análises de ofício e as demais alterações posteriores. É a etapa atual de trabalho.",
        },
      })[stage] || { label: "Sem QGC", hint: "nenhum credor cadastrado", help: "Este caso ainda não tem credores no QGC. Importe a lista inicial da recuperanda (Edital do art. 52) para começar." };
    },

    selectedCaseSummary() {
      return this.cases.find((item) => Number(item.id) === Number(this.selectedClientId)) || null;
    },

    selectedClientName() {
      const found = this.clients.find((item) => Number(item.id) === Number(this.selectedClientId))
        || this.cases.find((item) => Number(item.id) === Number(this.selectedClientId));
      return found ? found.name : "";
    },

    // Somente leitura so na etapa ja superada por uma mais nova: com o Edital 7
    // gerado e sem Consolidado, a etapa Edital 7 continua editavel.
    isReadOnly() {
      if (this.qgcVersion === 3) return false;
      return !!this.qgcCurrentStage && this.qgcVersion < this.qgcCurrentStage;
    },

    qgcVersionLabel() {
      const version = this.qgcVersions.find((item) => item.id === this.qgcVersion);
      return version ? version.label : `QGC v${this.qgcVersion}`;
    },

    qgcStageDefinitions() {
      return [
        { id: 1, label: "Edital Art. 52", description: "Lista inicial apresentada pela Recuperanda" },
        { id: 2, label: "Edital Art. 7", description: "Resultado após a conclusão das Hab/Div" },
        { id: 3, label: "QGC Consolidado", description: "Impugnações judiciais, ofícios e demais alterações" },
      ];
    },

    // Filtros de "Mais filtros" que estao preenchidos.
    moreFiltersCount() {
      return [this.filters.reference, this.filters.type, this.filters.currency, this.filters.source, this.filters.responsible_id, this.filters.has_conflict, this.filters.possible_duplicate, this.filters.is_overdue, this.filters.is_due_soon, this.filters.grouping_unconfirmed, this.filters.official_letter_pending, this.filters.has_documents, this.filters.creditor_analysis]
        .filter((value) => value !== null && value !== undefined && value !== "" && value !== false).length;
    },

    activeFilterCount() {
      return Object.values(this.filters).filter((value) => value !== null && value !== "" && value !== false).length;
    },

    hasFilters() {
      return this.activeFilterCount > 0;
    },

    primarySummary() {
      return [
        { key: "pending", label: "Créditos pendentes", value: this.summary.pending || 0, hint: "não iniciados", filter: { status: "pending" }, color: "grey" },
        { key: "in_analysis", label: "Em análise", value: this.summary.in_analysis || 0, hint: "em andamento", filter: { status: "analysis" }, color: "primary" },
        { key: "waiting_documents", label: "Aguardando documentos", value: this.summary.waiting_documents || 0, hint: "documentação", filter: { status: "waiting_documents" }, color: "warning" },
        { key: "ready", label: "Prontos para concluir", value: this.summary.ready || 0, hint: "fila de conclusão", filter: { status: "ready" }, color: "success" },
        { key: "conflicts", label: "Com conflito", value: this.summary.conflicts || 0, hint: "prioridade", filter: { has_conflict: true }, color: "error" },
        { key: "overdue", label: "Análises vencidas", value: this.summary.overdue || 0, hint: `${this.summary.due_soon || 0} vencem em breve`, filter: { is_overdue: true }, color: "error" },
      ];
    },

    secondarySummary() {
      return [
        { key: "total_creditors", label: "credores no caso", value: this.summary.total_creditors || 0 },
        { key: "total_credits", label: "créditos consolidados", value: this.summary.total_credits || 0 },
        { key: "possible_duplicates", label: "possíveis duplicidades", value: this.summary.possible_duplicates || 0, filter: { possible_duplicate: true } },
        { key: "unassigned", label: "sem responsável", value: this.summary.unassigned || 0, filter: { responsible_id: "unassigned" } },
      ];
    },

    quickFilters() {
      return [
        { key: "unassigned", label: "Sem responsável", value: this.summary.unassigned || 0, color: "deep-purple", filter: { responsible_id: "unassigned" } },
        { key: "conflicts", label: "Com conflito", value: this.summary.conflicts || 0, color: "error", filter: { has_conflict: true } },
        { key: "duplicates", label: "Possível duplicidade", value: this.summary.possible_duplicates || 0, color: "warning", filter: { possible_duplicate: true } },
        { key: "documents", label: "Aguardando documentos", value: this.summary.waiting_documents || 0, color: "warning", filter: { status: "waiting_documents" } },
        { key: "with_documents", label: "Com documentos", value: this.summary.with_documents || 0, color: "teal", filter: { has_documents: true } },
        { key: "ready", label: "Prontos para concluir", value: this.summary.ready || 0, color: "success", filter: { status: "ready" } },
        { key: "overdue", label: "Vencidos", value: this.summary.overdue || 0, color: "error", filter: { is_overdue: true } },
        { key: "due_soon", label: "Vencendo em breve", value: this.summary.due_soon || 0, color: "warning", filter: { is_due_soon: true } },
        { key: "creditors_concluded", label: "Credores concluídos", value: this.summary.creditors_concluded || 0, color: "success", filter: { creditor_analysis: "concluded" } },
        { key: "creditors_pending", label: "Credores pendentes", value: this.summary.creditors_pending || 0, color: "grey darken-1", filter: { creditor_analysis: "pending" } },
      ];
    },

    selectedCreditCount() {
      return this.selectedGroups.reduce((total, group) => total + group.credit_count, 0);
    },

    selectedCredits() {
      return this.selectedGroups.flatMap((group) => group.credits);
    },

    canSaveBatchDecisions() {
      const decisionsValid = this.decisionRows.length > 0 && this.decisionRows.every((row) => (
        row.outcome &&
        (!this.clientCompanies.length || !!row.client_company_id) &&
        (row.outcome !== "partial" || this.isPartialAmountValid(row))
      ));
      const overrideValid =
        !this.duplicateOverride ||
        (this.canOverrideDuplicates && this.duplicateJustification.trim().length >= 10);
      return decisionsValid && overrideValid;
    },

    canOverrideDuplicates() {
      const user = (this.$root && this.$root.user) || {};
      return Number(user.profile) === 1;
    },

    isDrawerPartialAmountValid() {
      const amount = Number(this.drawerDecision.recognized_amount);
      return Number.isFinite(amount) && amount >= 0;
    },

    canSaveDrawerDraft() {
      return this.drawerDecision.outcome !== "partial" || this.isDrawerPartialAmountValid;
    },

    selectedCreditRequestTargets() {
      if (!this.selectedCredit) return [];
      const targets = new Map();
      (this.selectedCredit.sources || [])
        .filter((source) => source.kind === "user_event" && source.reference_id && source.item_id)
        .forEach((source) => {
          const key = `${source.reference_id}:${source.item_id}`;
          if (!targets.has(key)) {
            targets.set(key, {
              eventId: Number(source.reference_id),
              itemId: Number(source.item_id),
              origin: source.business_origin || this.selectedCredit.reference || "Crédito sem referência",
              amount: source.amount,
              label: `Pedido #${source.reference_id} — ${source.business_origin || this.selectedCredit.reference || `crédito #${source.item_id}`}`,
            });
          }
        });
      return [...targets.values()];
    },

    canSendDocumentRequest() {
      const request = this.documentRequest;
      if (!request.target || (!request.generalDocs.length && !request.businessDocs.length)) return false;
      return !request.businessDocs.includes(50) || !!request.customName.trim();
    },

    allExpanded() {
      return this.groups.length > 0 && this.expandedGroups.length === this.groups.length;
    },

    comparisonOrigins() {
      if (!this.selectedCredit) return [];
      return [
        { key: "creditor", label: "Credor", amount: this.selectedCredit.values.creditor, detail: "Hab/Div" },
        { key: "debtor", label: "Recuperanda", amount: this.selectedCredit.values.debtor, detail: "Manifestação" },
        { key: "aj", label: "AJ", amount: this.selectedCredit.values.aj, detail: "Análise de Ofício" },
        { key: "edital_52", label: "Edital Art. 52", amount: this.qgcStageAmount(this.selectedCredit, 1), detail: "Lista inicial" },
        { key: "edital_7", label: "Edital Art. 7", amount: this.qgcStageAmount(this.selectedCredit, 2), detail: "Após Hab/Div" },
        { key: "consolidated", label: "Consolidado", amount: this.qgcStageAmount(this.selectedCredit, 3), detail: "Etapa posterior" },
      ];
    },
  },

  created() {
    this.selectedClientId = this.$route.query.client_id ? Number(this.$route.query.client_id) : null;
    this.loadClients();
    this.loadCases();
    this.loadAdmins();
    this.loadCreditorClasses();
    if (this.selectedClientId) this.load();
  },

  watch: {
    // Cards e "Todos os casos" navegam pela query (client_id); o voltar do navegador tambem.
    "$route.query.client_id"(value) {
      const id = value ? Number(value) : null;
      if (id === this.selectedClientId) return;
      this.selectedClientId = id;
      if (id) this.onClientChange();
      else this.loadCases();
    },

    selectedClientId(value, oldValue) {
      if (String(value) === String(oldValue)) return;

      this.loadCreditorClasses();
    },

    // Mascara de CPF/CNPJ das linhas do lote. Fica num watch, e nao numa
    // chamada de metodo no template, para o campo continuar sendo um v-model
    // simples: registrationMasked e o que aparece, registration guarda so os
    // digitos que vao para a API.
    "batchOfficialLetter.items": {
      deep: true,
      handler(items) {
        (items || []).forEach((item) => {
          const digits = String(item.registrationMasked || "").replace(/\D/g, "").slice(0, 14);

          let masked = digits;

          if (digits.length <= 11) {
            masked = digits
              .replace(/^(\d{3})(\d)/, "$1.$2")
              .replace(/^(\d{3})\.(\d{3})(\d)/, "$1.$2.$3")
              .replace(/\.(\d{3})(\d{1,2})$/, ".$1-$2");
          } else {
            masked = digits
              .replace(/^(\d{2})(\d)/, "$1.$2")
              .replace(/^(\d{2})\.(\d{3})(\d)/, "$1.$2.$3")
              .replace(/\.(\d{3})(\d)/, ".$1/$2")
              .replace(/(\d{4})(\d{1,2})$/, "$1-$2");
          }

          if (item.registration !== digits) item.registration = digits;
          if (item.registrationMasked !== masked) item.registrationMasked = masked;
        });
      },
    },
  },

  methods: {
    async loadCreditorClasses() {
      // Inclui ocultas: creditos ja analisados mantem o nome da classe.
      this.creditorTypes = await creditorClassUtil.listLabels(this.selectedClientId);
    },

    creditorName(value) {
      return String(value || "Credor não identificado").toLocaleUpperCase("pt-BR");
    },

    formatPercentage(value) {
      return `${Number(value || 0).toLocaleString("pt-BR", {
        minimumFractionDigits: 0,
        maximumFractionDigits: 1,
      })}%`;
    },

    // Tela inicial: resumo de cada caso para os cards.
    async loadCases() {
      this.loadingCases = true;
      try {
        const response = await axios.get("/api/admin/credit-analysis/cases");
        this.cases = response.data.content || [];
      } catch (error) {
        this.notifyError(error);
      } finally {
        this.loadingCases = false;
      }
    },

    openCase(item) {
      this.$router.push({ path: this.$route.path, query: { client_id: item.id } }).catch(() => {});
    },

    backToCases() {
      this.$router.push({ path: this.$route.path }).catch(() => {});
    },

    async loadClients() {
      this.loadingClients = true;
      try {
        const response = await axios.get("/api/clients", { params: { start: 1, limit: 100000, search: {} } });
        this.clients = response.data.content.data || [];
      } catch (error) {
        this.notifyError(error);
      } finally {
        this.loadingClients = false;
      }
    },

    async loadAdmins() {
      try {
        const response = await axios.get("/api/admin/list", { params: { page: 1, limit: 1000 } });
        this.admins = (response.data.content.data || []).map((admin) => ({ id: admin.id, name: admin.name }));
      } catch (error) {
        console.error(error);
      }
    },

    onClientChange() {
      this.caseTab = 0;
      this.tableOptions.page = 1;
      this.groups = [];
      this.clientCompanies = [];
      this.effectiveQgcVersion = null;
      this.qgcCurrentStage = null;
      this.pendingIdentification = [];
      this.summary = {};
      this.selectedGroups = [];
      this.expandedGroups = [];
      if (this.selectedClientId) {
        this.$router.replace({ path: this.$route.path, query: { client_id: this.selectedClientId } }).catch(() => {});
        this.load();
      }
    },

    onIncludeQgcChange() {
      this.tableOptions.page = 1;
      this.selectedGroups = [];
      this.expandedGroups = [];
      this.load();
    },

    async load() {
      if (!this.selectedClientId || this.loading) return;
      this.loading = true;
      try {
        const search = Object.fromEntries(
          Object.entries(this.filters).filter(([, value]) => value !== null && value !== "" && value !== false)
        );
        const response = await axios.get("/api/admin/credit-analysis", {
          params: {
            client_id: this.selectedClientId,
            version: this.qgcVersion,
            include_qgc: this.includeQgc,
            sort: this.sortMode,
            page: this.tableOptions.page,
            limit: this.tableOptions.itemsPerPage,
            search,
          },
        });
        const content = response.data.content;
        this.groups = content.data || [];
        this.clientCompanies = (content.context && content.context.client_companies) || [];
        this.habdivDeadline = (content.context && content.context.habdiv_deadline) || null;
        this.effectiveQgcVersion = content.context && content.context.qgc_effective_version;
        this.qgcCurrentStage = (content.context && content.context.qgc_current_stage) || null;
        this.loadCases();
        this.summary = content.summary || {};
        this.pendingIdentification = content.pending_identification || [];
        this.paging = content.paging;
        this.sortGroups();
      } catch (error) {
        this.notifyError(error);
      } finally {
        this.loading = false;
      }
    },

    async assignResponsible(creditKeys, responsibleId) {
      if (!creditKeys.length || !responsibleId) return;
      this.assigning = true;
      try {
        await axios.post("/api/admin/credit-analysis/assign-responsible", {
          client_id: this.selectedClientId,
          qgc_version: this.qgcVersion,
          credit_keys: creditKeys,
          responsible_admin_id: responsibleId,
        });
        this.$dialog.notify.success("Responsável atribuído com sucesso.", {
          position: "top-right",
          timeout: 3000,
        });
        this.assignDialog = false;
        this.selectedResponsibleId = null;
        await this.load();
        if (this.selectedCredit) {
          const refreshed = this.groups
            .flatMap((group) => group.credits)
            .find((credit) => credit.key === this.selectedCredit.key);
          this.selectedCredit = refreshed || this.selectedCredit;
        }
      } catch (error) {
        this.notifyError(error);
      } finally {
        this.assigning = false;
      }
    },

    assignSelectedResponsible() {
      return this.assignResponsible(this.selectedCredits.map((credit) => credit.key), this.selectedResponsibleId);
    },

    assignToMe() {
      const currentAdminId = this.$root.user && this.$root.user.id;
      if (!currentAdminId) {
        this.$dialog.notify.error("Não foi possível identificar o administrador logado.", {
          position: "top-right",
          timeout: 5000,
        });
        return;
      }
      return this.assignResponsible([this.selectedCredit.key], currentAdminId);
    },

    openDocumentRequest() {
      const targets = this.selectedCreditRequestTargets;
      if (!targets.length) return;
      this.documentRequest = {
        dialog: true,
        saving: false,
        target: targets.length === 1 ? targets[0] : null,
        generalDocs: [],
        businessDocs: [],
        customName: "",
      };
    },

    closeDocumentRequest() {
      if (this.documentRequest.saving) return;
      this.documentRequest.dialog = false;
    },

    async sendDocumentRequest() {
      if (!this.canSendDocumentRequest) return;
      const request = this.documentRequest;
      const target = request.target;
      const docList = [];

      if (request.generalDocs.length) {
        docList.push({
          user_event_id: target.eventId,
          docs: request.generalDocs.map((id) => ({ id, uploaded: false })),
        });
      }
      if (request.businessDocs.length) {
        docList.push({
          user_event_item_id: target.itemId,
          origin: target.origin,
          amount: target.amount,
          docs: request.businessDocs.map((id) => ({
            id,
            uploaded: false,
            ...(id === 50 ? { customName: request.customName.trim() } : {}),
          })),
        });
        // The existing notification formatter uses this marker to introduce
        // the item-specific supporting documents.
        docList.push({
          user_event_id: target.eventId,
          docs: [{ id: 3, uploaded: false }],
        });
      }

      request.saving = true;
      try {
        await axios.patch(`/api/admin/user-events/creditor-doc-request/${target.eventId}`, { docList });
        this.$dialog.notify.success("Documentos solicitados com sucesso.", {
          position: "top-right",
          timeout: 3000,
        });
        request.dialog = false;
        const creditKey = this.selectedCredit && this.selectedCredit.key;
        await this.load();
        if (creditKey) {
          this.selectedCredit = this.groups
            .flatMap((group) => group.credits || [])
            .find((credit) => credit.key === creditKey) || this.selectedCredit;
        }
      } catch (error) {
        this.notifyError(error);
      } finally {
        request.saving = false;
      }
    },

    openBatchDecision() {
      this.decisionConflict = null;
      this.duplicateOverride = false;
      this.duplicateJustification = "";
      this.decisionRows = this.selectedCredits.map((credit) => {
        const draft = credit.analysis_draft || {};
        return {
          credit,
          outcome: draft.outcome || null,
          recognized_amount: draft.recognized_amount !== undefined
            ? draft.recognized_amount
            : null,
          client_company_id:
            draft.client_company_id ||
            credit.client_company_id ||
            (this.clientCompanies.length === 1 ? this.clientCompanies[0].id : null),
          company_name: draft.company_name || credit.company_name,
        };
      });
      this.decisionRows.forEach((row) => this.applyDecisionCompany(row));
      this.decisionDialog = true;
    },

    applyDecisionOutcome(row) {
      if (row.outcome === "rejected") {
        row.recognized_amount = 0;
        return;
      }
      if (row.outcome === "approved") {
        row.recognized_amount =
          row.credit.values.aj ??
          row.credit.values.debtor ??
          row.credit.values.creditor ??
          row.credit.values.qgc;
      }
    },

    isPartialAmountValid(row) {
      const amount = Number(row.recognized_amount);
      return Number.isFinite(amount) && amount >= 0;
    },

    applyDecisionCompany(row) {
      const company = this.clientCompanies.find(
        (item) => Number(item.id) === Number(row.client_company_id)
      );
      if (company) row.company_name = company.name;
    },

    async saveBatchDecisions() {
      if (!this.canSaveBatchDecisions) return;
      this.savingDecisions = true;
      try {
        const blockedKeys = new Set(
          this.decisionConflict
            ? this.decisionConflict.blockers.map((blocker) => blocker.credit_key)
            : []
        );
        const decisions = this.decisionRows.map((row) => {
          const credit = row.credit;
          const existingDecision = credit.analysis_draft || {};
          const shouldOverride = this.duplicateOverride && blockedKeys.has(credit.key);
          return {
            client_id: this.selectedClientId,
            credit_key: credit.key,
            qgc_version: this.qgcVersion,
            duplicate_override: shouldOverride,
            duplicate_justification: shouldOverride ? this.duplicateJustification.trim() : null,
            decision: Object.assign({}, existingDecision, {
              outcome: row.outcome,
              recognized_amount: row.recognized_amount,
              creditor_type: existingDecision.creditor_type || credit.creditor_type,
              currency: existingDecision.currency || credit.currency,
              client_company_id: row.client_company_id || null,
              company_name: row.company_name || credit.company_name,
              reference: existingDecision.reference || credit.reference || "",
              qgc_action: existingDecision.qgc_action || (credit.qgc_count ? "update" : "create"),
            }),
          };
        });
        await axios.post("/api/admin/credit-analysis/conclude-batch", { decisions });
        this.$dialog.notify.success("Análises concluídas com sucesso.", {
          position: "top-right",
          timeout: 3500,
        });
        this.decisionDialog = false;
        await this.load();
      } catch (error) {
        const response = error.response && error.response.data;
        const content = response && response.content;
        if (error.response && error.response.status === 409 && content) {
          this.decisionConflict = {
            message: content.error && content.error.message
              ? content.error.message
              : "Existem possíveis duplicidades no QGC.",
            blockers: content.blockers || [],
          };
          this.$dialog.notify.error(this.decisionConflict.message, {
            position: "top-right",
            timeout: 6000,
          });
        } else {
          this.notifyError(error);
        }
      } finally {
        this.savingDecisions = false;
      }
    },

    applyFilters() {
      this.tableOptions.page = 1;
      this.load();
    },

    // "Sem pedido" sao os creditos so do QGC: liga a inclusao deles na lista.
    onRequestTypeChange(value) {
      if (value === "without_request" && !this.includeQgc) this.includeQgc = true;
      this.applyFilters();
    },

    filterDebounced: _.debounce(function () {
      this.applyFilters();
    }, 400),

    clearFilters() {
      this.filters = emptyFilters();
      this.tableOptions.page = 1;
      this.load();
    },

    applySummaryFilter(item) {
      if (!item.filter) return;
      const active = Object.entries(item.filter).every(([key, value]) => this.filters[key] === value);
      this.filters = emptyFilters();
      if (!active) Object.assign(this.filters, item.filter);
      this.tableOptions.page = 1;
      this.load();
    },

    // .zip dos documentos da analise de oficio: do caso inteiro ou de um credor.
    openArchiveDialog(creditor = null) {
      this.archiveCreditor = creditor;
      this.archiveDialog = true;
    },

    creditorReviewHint(item) {
      if (item.analysis_concluded_auto) return "Concluída automaticamente: todos os créditos deste credor têm a análise concluída. Para reabrir, reabra a análise de algum crédito.";
      if (item.analysis_concluded) {
        const review = item.analysis_review || {};
        const who = review.admin_name ? ` por ${review.admin_name}` : "";
        const when = review.concluded_at ? ` em ${this.$options.filters.defaultDate(review.concluded_at, "DD/MM/YYYY HH:mm")}` : "";
        return `Marcada como concluída${who}${when}. Clique para desmarcar.`;
      }
      return "Clique para marcar a análise deste credor como concluída. Ela também é marcada sozinha quando todos os créditos do credor têm a análise concluída.";
    },

    async toggleCreditorReview(item) {
      if (item.analysis_concluded_auto) {
        this.$dialog.notify.info("Este credor foi concluído automaticamente: todos os créditos dele têm a análise concluída.", { position: "top-right", timeout: 4000 });
        return;
      }
      if (this.isReadOnly) {
        this.$dialog.notify.warning("Versões anteriores do QGC são somente para consulta.", { position: "top-right", timeout: 3000 });
        return;
      }
      const concluded = !item.analysis_concluded;
      this.savingReviewKey = item.key;
      try {
        const response = await axios.post("/api/admin/credit-analysis/creditor-review", {
          client_id: this.selectedClientId,
          creditor_key: item.key,
          concluded,
        });
        const result = response.data.content || {};
        item.analysis_concluded = concluded;
        item.analysis_review = concluded ? { concluded_at: result.concluded_at, admin_name: result.admin_name } : null;
        const delta = concluded ? 1 : -1;
        this.summary = Object.assign({}, this.summary, {
          creditors_concluded: (this.summary.creditors_concluded || 0) + delta,
          creditors_pending: (this.summary.creditors_pending || 0) - delta,
        });
      } catch (error) {
        this.$dialog.notify.error(this.$codes(error), { position: "top-right", timeout: 4000 });
      } finally {
        this.savingReviewKey = null;
      }
    },

    // Credito sem hab/div: so do QGC ou com analise de oficio aberta.
    isOfficialCredit(credit) {
      return !!credit && (credit.qgc_only || (credit.sources || []).some((source) => source.kind === "official_letter"));
    },

    // Arquivos recebidos sobre o credor (todos os creditos dele).
    groupDocumentsCount(group) {
      return (group.credits || []).reduce((total, credit) => total + (Number(credit.documents_count) || 0), 0);
    },

    isQuickFilterActive(item) {
      return item.filter && Object.entries(item.filter).every(([key, value]) => this.filters[key] === value);
    },

    onPageChange(page) {
      this.tableOptions.page = page;
      this.load();
    },

    // A troca de tamanho e seguida de update:page(1), que recarrega.
    setPageSize(size) {
      this.tableOptions.itemsPerPage = size;
    },

    onPageSizeChange(size) {
      if (!size) return;
      this.tableOptions.itemsPerPage = size;
      this.tableOptions.page = 1;
      this.load();
    },

    openCredit(credit) {
      this.selectedCredit = credit;
      const draft = credit.analysis_draft || {};
      this.drawerDecision = Object.assign({
        outcome: credit.decision_outcome || null,
        recognized_amount:
          credit.values.aj ??
          credit.values.debtor ??
          credit.values.creditor ??
          credit.values.qgc,
        creditor_type: credit.creditor_type,
        currency: credit.currency,
        client_company_id: credit.client_company_id || null,
        company_name: credit.company_name,
        reference: credit.reference || "",
        qgc_action: credit.qgc_count ? "update" : "create",
        related_requests_action: "joint",
        reason: "",
      }, draft);
      this.drawerTab = 0;
      this.drawer = true;
    },

    applyDrawerDecisionOutcome(outcome) {
      if (!this.selectedCredit) return;
      if (outcome === "rejected") {
        this.drawerDecision.recognized_amount = 0;
        return;
      }
      if (outcome === "approved") {
        this.drawerDecision.recognized_amount =
          this.selectedCredit.values.aj ??
          this.selectedCredit.values.debtor ??
          this.selectedCredit.values.creditor ??
          this.selectedCredit.values.qgc;
      }
    },

    async saveDrawerDraft() {
      if (!this.selectedCredit || !this.canSaveDrawerDraft) return;
      this.savingDrawerDraft = true;
      try {
        const creditKey = this.selectedCredit.key;
        await axios.put("/api/admin/credit-analysis/draft", {
          client_id: this.selectedClientId,
          credit_key: creditKey,
          qgc_version: this.qgcVersion,
          decision: this.drawerDecision,
        });
        this.selectedCredit.analysis_draft = Object.assign({}, this.drawerDecision);
        if (!["approved", "rejected", "partial"].includes(this.selectedCredit.status)) {
          this.selectedCredit.status = "ready";
        }
        this.$dialog.notify.success("Rascunho da análise salvo com sucesso.", {
          position: "top-right",
          timeout: 3000,
        });
      } catch (error) {
        this.notifyError(error);
      } finally {
        this.savingDrawerDraft = false;
      }
    },

    onQgcVersionChange() {
      this.tableOptions.page = 1;
      this.load();
    },

    returnToActiveVersion() {
      this.qgcVersion = 3;
      this.load();
    },

    openCaseAudit() {
      this.$router.push({ path: "/auditoria", query: { client_id: this.selectedClientId } });
    },

    async exportView() {
      const rows = [
        ["Credor", "CPF/CNPJ", "Empresa devedora", "Classe", "Moeda", "Referência", "Credor", "Recuperanda", "AJ", "Edital 52", "Edital 7", "Consolidado", "Status"],
      ];
      this.groups.forEach((group) => {
        group.credits.forEach((credit) => {
          rows.push([
            this.creditorName(group.name),
            group.registration,
            credit.company_name,
            credit.creditor_type_label,
            credit.currency_code,
            credit.reference || "",
            credit.values.creditor,
            credit.values.debtor,
            credit.values.aj,
            this.qgcStageAmount(credit, 1),
            this.qgcStageAmount(credit, 2),
            this.qgcStageAmount(credit, 3),
            this.statusLabel(credit.status),
          ]);
        });
      });
      await downloadXlsx(rows, `central-analise-creditos-caso-${this.selectedClientId}`, { sheetName: 'Central' });
    },

    toggleExpandAll() {
      this.expandedGroups = this.allExpanded ? [] : [...this.groups];
    },

    onSortChange() {
      this.tableOptions.page = 1;
      this.load();
    },

    sortGroups() {
      const firstCredit = (group) => group.credits[0] || {};
      const sorters = {
        priority: (a, b) => Number(b.has_conflict || b.possible_duplicate) - Number(a.has_conflict || a.possible_duplicate),
        class: (a, b) => (a.class_ids[0] || 999) - (b.class_ids[0] || 999),
        deadline: (a, b) => (firstCredit(a).deadline_days ?? 99999) - (firstCredit(b).deadline_days ?? 99999),
        overdue: (a, b) => Number(firstCredit(b).is_overdue) - Number(firstCredit(a).is_overdue),
        activity: (a, b) => String(firstCredit(b).last_activity_at || "").localeCompare(String(firstCredit(a).last_activity_at || "")),
        status: (a, b) => String(firstCredit(a).status || "").localeCompare(String(firstCredit(b).status || "")),
        duplicates: (a, b) => Number(b.possible_duplicate) - Number(a.possible_duplicate),
      };
      this.groups = [...this.groups].sort(sorters[this.sortMode] || sorters.priority);
    },

    openFullAnalysis() {
      if (!this.selectedCredit) return;
      this.$router.push({
        path: "/central-analise-creditos/analise",
        query: {
          client_id: this.selectedClientId,
          version: this.qgcVersion,
          key: this.selectedCredit.key,
        },
      });
    },

    // Valor de referencia do credito, na ordem de prioridade usada na Central.
    resolveCreditAmount(credit) {
      if (!credit || !credit.values) return null;

      return credit.values.aj
        ?? credit.values.debtor
        ?? credit.values.creditor
        ?? credit.values.qgc
        ?? null;
    },

    // Desativado: metodos do modal de analise de oficio individual.
    // // Identificacao do credor para o oficio.
    // //
    // // Creditos vindos apenas de hab/div nao tem registro no QGC, entao nao ha
    // // user_creditor_id. Nesse caso o backend cria o credor a partir dos dados
    // // do proprio credito, pelo caminho new_creditor_*.
    // buildCreditorPayload(credit, creditor = null) {
      // if (creditor && creditor.id) return { user_creditor_id: creditor.id };

      // return {
        // new_creditor_name: credit.creditor_name,
        // new_creditor_registration: credit.registration,
        // new_creditor_client_id: this.selectedClientId,
        // new_creditor_client_company_id: credit.client_company_id || undefined,
        // new_creditor_type: credit.creditor_type || undefined,
        // new_creditor_currency: credit.currency || undefined,
        // new_creditor_version: this.qgcVersion || undefined,
      // };
    // },

    // // Oficio ja existente no credito, para editar em vez de duplicar.
    // findExistingOfficialLetter(credit) {
      // return (credit && credit.sources ? credit.sources : [])
        // .find((source) => source.kind === "official_letter") || null;
    // },

    // openOfficialLetterAnalysis() {
      // if (!this.selectedCredit) return;

      // const existingCreditor = (this.selectedCredit.qgc || [])[0];
      // const existingLetter = this.findExistingOfficialLetter(this.selectedCredit);

      // this.officialLetter = {
        // dialog: true,
        // saving: false,
        // // Havendo oficio no credito, a modal edita esse registro. Criar outro
        // // duplicaria a analise do mesmo credito.
        // id: existingLetter ? existingLetter.reference_id : null,
        // creditKey: this.selectedCredit.key,
        // creditorName: this.selectedCredit.creditor_name,
        // creditorPayload: this.buildCreditorPayload(this.selectedCredit, existingCreditor),
        // amount: existingLetter
          // ? existingLetter.amount
          // : this.resolveCreditAmount(this.selectedCredit),
        // reason: "",
      // };

      // if (existingLetter) this.loadOfficialLetterReason(existingLetter.reference_id);
    // },

    // async loadOfficialLetterReason(id) {
      // try {
        // const result = await axios.get(`/api/admin/official-letters/${id}`);
        // const content = result.data.content || {};

        // this.officialLetter.reason = content.reason || "";
      // } catch (error) {
        // // Sem o texto atual a modal segue utilizavel para preenche-lo.
      // }
    // },

    // async saveOfficialLetter() {
      // const letter = this.officialLetter;

      // if (letter.amount === null || letter.amount === undefined || letter.amount === "") {
        // this.$dialog.notify.error("Informe o valor da análise de ofício.", { position: "top-right", timeout: 3000 });
        // return;
      // }

      // letter.saving = true;

      // try {
        // const payload = {
          // ...(letter.creditorPayload || {}),
          // amount: letter.amount,
          // credit_analysis_key: letter.creditKey,
          // reason: letter.reason || undefined,
        // };

        // if (letter.id) {
          // await axios.patch(`/api/admin/official-letters/${letter.id}`, payload);
        // } else {
          // await axios.post("/api/admin/official-letters", payload);
        // }

        // this.$dialog.notify.success(
          // letter.id ? "Análise de ofício atualizada." : "Análise de ofício incluída.",
          // { position: "top-right", timeout: 3000 }
        // );

        // letter.dialog = false;
        // await this.load();
      // } catch (error) {
        // this.$dialog.notify.error(this.$codes(error) || "Erro ao salvar a análise de ofício.", { position: "top-right", timeout: 3000 });
      // } finally {
        // letter.saving = false;
      // }
    // },

    openBatchOfficialLetter() {
      const credits = this.selectedGroups.flatMap((group) => group.credits || []);

      // Classe, moeda e valor entram como referencia e ficam editaveis: a
      // analise de oficio existe justamente para o AJ definir valores
      // diferentes dos apresentados.
      const items = credits.map((credit, index) => {
        const creditor = (credit.qgc || [])[0];

        return {
          rowId: `credit-${index}`,
          isNew: false,
          credit_analysis_key: credit.key,
          user_creditor_id: creditor && creditor.id ? creditor.id : null,
          creditor_name: credit.creditor_name,
          registration: credit.registration,
          registrationMasked: credit.registration,
          client_company_id: credit.client_company_id || null,
          type: credit.creditor_type || null,
          currency: credit.currency || 1,
          amount: this.resolveCreditAmount(credit),
        };
      });

      this.batchOfficialLetter = {
        dialog: true,
        saving: false,
        reason: "",
        rowSeq: items.length,
        items,
      };
    },

    nextBatchRowId() {
      this.batchOfficialLetter.rowSeq = (this.batchOfficialLetter.rowSeq || 0) + 1;
      return `row-${this.batchOfficialLetter.rowSeq}`;
    },

    // Credor que ainda nao esta no QGC: a analise de oficio tambem serve para
    // inclui-lo no quadro.
    addBatchCreditorRow() {
      this.batchOfficialLetter.items.push({
        rowId: this.nextBatchRowId(),
        isNew: true,
        credit_analysis_key: null,
        user_creditor_id: null,
        creditor_name: "",
        registration: "",
        registrationMasked: "",
        client_company_id: null,
        type: null,
        currency: 1,
        amount: null,
      });
    },

    // Outro credito do mesmo credor, necessariamente em outra classe: no QGC
    // creditos da mesma classe sao somados num registro so.
    addBatchCreditRow(item) {
      const index = this.batchOfficialLetter.items.indexOf(item);

      this.batchOfficialLetter.items.splice(index + 1, 0, {
        rowId: this.nextBatchRowId(),
        // Sem credit_analysis_key: e um credito novo, nao o da Central.
        isNew: !item.user_creditor_id,
        credit_analysis_key: null,
        user_creditor_id: item.user_creditor_id,
        creditor_name: item.creditor_name,
        registration: item.registration,
        registrationMasked: item.registrationMasked || item.registration,
        client_company_id: item.client_company_id,
        type: null,
        currency: item.currency || 1,
        amount: null,
      });
    },

    removeBatchRow(index) {
      this.batchOfficialLetter.items.splice(index, 1);
    },

    buildBatchItemPayload(item) {
      const payload = {
        amount: item.amount,
        creditor_name: item.creditor_name,
        credit_analysis_key: item.credit_analysis_key || undefined,
      };

      // Credor ja no QGC: user_creditor_type leva a classe escolhida, que o
      // backend aplica ao credor.
      if (item.user_creditor_id && item.credit_analysis_key) {
        payload.user_creditor_id = item.user_creditor_id;
        payload.user_creditor_type = item.type;
        return payload;
      }

      // Demais casos (credor novo, ou credito extra em outra classe) usam o
      // caminho new_creditor_*, que reaproveita o credor existente quando ja
      // houver um com o mesmo CPF/CNPJ, classe e moeda.
      payload.new_creditor_name = item.creditor_name;
      payload.new_creditor_registration = String(item.registration || "").replace(/\D/g, "");
      payload.new_creditor_client_id = this.selectedClientId;
      payload.new_creditor_type = item.type;
      payload.new_creditor_currency = item.currency || 1;
      payload.new_creditor_version = this.qgcVersion || undefined;

      if (item.client_company_id) payload.new_creditor_client_company_id = item.client_company_id;

      return payload;
    },

    async saveBatchOfficialLetter() {
      const batch = this.batchOfficialLetter;

      if (!batch.items.length) return;

      const invalida = batch.items.find((item) => {
        const semCredor = !String(item.creditor_name || "").trim() || !String(item.registration || "").trim();
        const semValor = item.amount === null || item.amount === undefined || item.amount === "";
        return semCredor || !item.type || semValor;
      });

      if (invalida) {
        this.$dialog.notify.error(
          "Preencha credor, CPF/CNPJ, classe e valor em todas as linhas.",
          { position: "top-right", timeout: 4000 }
        );
        return;
      }

      batch.saving = true;

      try {
        const result = await axios.post("/api/admin/official-letters/batch", {
          reason: batch.reason || undefined,
          items: batch.items.map((item) => this.buildBatchItemPayload(item)),
        });

        const content = result.data.content || {};

        if (content.failed_count) {
          this.$dialog.notify.warning(
            `${content.created_count} análise(s) incluída(s), ${content.failed_count} com erro.`,
            { position: "top-right", timeout: 6000 }
          );
        } else {
          this.$dialog.notify.success(
            `${content.created_count} análise(s) de ofício incluída(s).`,
            { position: "top-right", timeout: 3000 }
          );
        }

        batch.dialog = false;
        this.selectedGroups = [];
        await this.load();
      } catch (error) {
        this.$dialog.notify.error(this.$codes(error) || "Erro ao incluir as análises de ofício.", { position: "top-right", timeout: 3000 });
      } finally {
        batch.saving = false;
      }
    },

    openSource(source) {
      if (source.kind === "official_letter") {
        this.$router.push(`/analise-oficio/${source.reference_id}`);
      } else {
        this.$router.push(`/divergencias/${source.reference_id}`);
      }
    },

    formatRegistration(value) {
      const digits = String(value || "").replace(/\D/g, "");
      if (digits.length === 11) return digits.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, "$1.$2.$3-$4");
      if (digits.length === 14) return digits.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, "$1.$2.$3/$4-$5");
      return value || "CPF/CNPJ não informado";
    },

    formatAmount(value, currency) {
      if (value === null || value === undefined) return "—";
      const symbols = { BRL: "R$", USD: "US$", EUR: "€" };
      return `${symbols[currency] || currency} ${Number(value).toLocaleString("pt-BR", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}`;
    },

    qgcStageAmount(credit, version) {
      const stage = credit && credit.qgc_stages && credit.qgc_stages[version];
      return stage ? stage.amount : null;
    },

    qgcStageCount(credit, version) {
      const stage = credit && credit.qgc_stages && credit.qgc_stages[version];
      return stage ? Number(stage.count) || 0 : 0;
    },

    sourceLabel(origin) {
      return ({ creditor: "Credor", debtor: "Recuperanda", aj: "AJ" })[origin] || origin;
    },

    sourceColor(origin) {
      return ({ creditor: "primary", debtor: "deep-purple", aj: "teal" })[origin] || "grey";
    },

    itemDocuments(source) {
      return (this.selectedCredit && this.selectedCredit.documents || []).filter(
        (document) => Number(document.source_item_id) === Number(source.item_id)
      );
    },

    itemStatusLabel(status) {
      return ({ 0: "Pendente", 1: "Aprovado", 2: "Rejeitado", 3: "Parcial", 4: "Documentos solicitados", 5: "Documentos enviados" })[Number(status)] || "Pendente";
    },

    itemStatusColor(status) {
      return ({ 1: "success", 2: "error", 3: "warning", 4: "orange", 5: "info" })[Number(status)] || "grey";
    },

    // Cor do status do pedido de hab/div, usada no selo e no botao de abrir o pedido.
    requestStatusColor(status) {
      return ({ 0: "orange darken-1", 1: "success", 2: "error", 3: "warning darken-1", 4: "deep-orange", 5: "info" })[Number(status)] || "grey darken-1";
    },

    openSourceNewTab(source) {
      const path = source.kind === "official_letter" ? `/analise-oficio/${source.reference_id}` : `/divergencias/${source.reference_id}`;
      const route = this.$router.resolve(path);
      window.open(route.href, "_blank", "noopener");
    },

    // Carrega todos os grupos do filtro atual (sem paginacao) e seleciona.
    async selectAllMatching() {
      if (!this.selectedClientId || this.selectingAll) return;
      this.selectingAll = true;
      try {
        const search = Object.fromEntries(
          Object.entries(this.filters).filter(([, value]) => value !== null && value !== "" && value !== false)
        );
        const response = await axios.get("/api/admin/credit-analysis", {
          params: {
            client_id: this.selectedClientId,
            version: this.qgcVersion,
            include_qgc: this.includeQgc,
            sort: this.sortMode,
            all: 1,
            search,
          },
        });
        const groups = (response.data.content && response.data.content.data) || [];
        this.selectedGroups = groups;
        this.$dialog.notify.info(`${groups.length} credor(es) selecionado(s).`, { position: "top-right", timeout: 3000 });
      } catch (error) {
        this.notifyError(error);
      } finally {
        this.selectingAll = false;
      }
    },

    // Prorrogacao do prazo das solicitacoes de documentos do caso (todas as do caso, em lote).
    openDeadlineDialog() {
      this.deadlineDialog = true;
    },

    onDebtorRequestSent() {
      this.load();
    },

    // Solicitacao de documentos ao credor ou a devedora para os creditos selecionados.
    openDocumentRequestDialog(target) {
      this.documentRequestTarget = target === "client" ? "client" : "creditor";
      this.debtorRequestDialog = true;
    },

    statusLabel(status) {
      const decisionLabel = ({
        approved: "Aprovado",
        rejected: "Rejeitado",
        partial: "Aprovado parcialmente",
      })[status];
      if (decisionLabel) return decisionLabel;
      const option = this.statusOptions.find((item) => item.value === status);
      return option ? option.text : status;
    },

    qgcVersionShortLabel(version) {
      return ({ 1: "Edital 52", 2: "Edital 7", 3: "QGC Consolidado" })[Number(version)] || `QGC v${version}`;
    },

    statusColor(status) {
      return ({
        not_started: "grey",
        triage: "blue-grey",
        analysis: "primary",
        waiting_documents: "warning",
        conflict: "error",
        ready: "success",
        concluded: "success",
        qgc_only: "info",
        approved: "success",
        rejected: "error",
        partial: "teal",
      })[status] || "grey";
    },

    outcomeLabel(outcome) {
      return ({
        approved: "Aprovado",
        rejected: "Rejeitado",
        partial: "Aprovado parcialmente",
      })[outcome] || outcome;
    },

    deadlineLabel(credit) {
      if (credit.deadline_days === null) return "Sem prazo";
      if (credit.deadline_days < 0) return `${Math.abs(credit.deadline_days)} d atrasado`;
      if (credit.deadline_days === 0) return "Vence hoje";
      return `${credit.deadline_days} d`;
    },

    alertLabel(alert) {
      return ({
        value_conflict: "Divergência de valor",
        possible_duplicate: "Possível duplicidade",
        grouping_unconfirmed: "Agrupamento não confirmado",
        unassigned: "Sem responsável",
        overdue: "Prazo vencido",
        due_soon: "Vencendo em breve",
        stale: "Parado há mais de 15 dias",
        official_letter_pending: "Análise de ofício pendente",
      })[alert] || alert;
    },

    alertIcon(alert) {
      return ({
        value_conflict: "mdi-alert-outline",
        possible_duplicate: "mdi-content-duplicate",
        grouping_unconfirmed: "mdi-link-variant",
        unassigned: "mdi-account-question-outline",
        overdue: "mdi-clock-alert-outline",
        due_soon: "mdi-clock-fast",
        stale: "mdi-progress-clock",
        official_letter_pending: "mdi-file-document-alert-outline",
      })[alert] || "mdi-information-outline";
    },

    alertColor(alert) {
      if (["value_conflict", "overdue"].includes(alert)) return "error";
      if (["possible_duplicate", "due_soon", "stale", "official_letter_pending"].includes(alert)) return "warning";
      return "blue-grey";
    },

    notifyError(error) {
      console.error(error);
      this.$dialog.notify.error(this.$codes(error), { position: "top-right", timeout: 5000 });
    },
  },
};
</script>

<style lang="scss" scoped>
@import "../../assets/scss/ui/tokens";

// Tons de status do padrao: [fundo, texto, ponto]
$tone-success: map-get($ui-tones, success);
$tone-warning: map-get($ui-tones, warning);
$tone-purple: map-get($ui-tones, purple);

.summary-card {
  border: 1px solid $ui-border;
}

.context-hint {
  color: $ui-muted;
  font-size: 12px;
}

.work-version {
  background: $ui-bg-muted;
  border: 1px solid $ui-border;
  border-radius: 6px;
  display: flex;
  flex-direction: column;
  min-height: 40px;
  padding: 5px 10px;
}

.work-version span,
.work-version small {
  color: $ui-muted;
  font-size: 9px;
}

.work-version strong {
  color: $ui-text;
  font-size: 12px;
}

.current-stage strong {
  display: flex;
  align-items: center;
  font-size: 14px;
}

.read-only-banner {
  margin: 0 0 12px;
}

.summary-card {
  cursor: pointer;
  min-height: 116px;
  transition: border-color 0.2s, transform 0.2s;
}

.summary-card:hover,
.summary-card.active {
  border-color: var(--v-primary-base);
  transform: translateY(-1px);
}

.summary-card .v-card__text {
  display: flex;
  flex-direction: column;
  height: 100%;
}

.summary-label {
  color: $ui-muted;
  font-size: 12px;
}

.summary-card strong {
  font-size: 28px;
  line-height: 1.25;
  margin-top: 8px;
}

.summary-card small {
  color: $ui-subtle;
}

.secondary-summary {
  align-items: baseline;
  background: $ui-bg;
  border: 1px solid $ui-border;
  border-radius: 6px;
  color: $ui-text;
  display: flex;
  gap: 7px;
  padding: 10px 12px;
  text-align: left;
  width: 100%;
}

.secondary-summary strong {
  font-size: 18px;
}

.secondary-summary span {
  color: $ui-muted;
  font-size: 11px;
}

.review-progress-card {
  align-items: center;
  border: 1px solid $ui-border;
  display: grid;
  gap: 16px 24px;
  grid-template-columns: minmax(260px, 1fr) auto;
  padding: 14px 16px;
}

.review-progress-card .v-progress-linear {
  grid-column: 1 / -1;
}

.review-progress-copy {
  display: flex;
  flex-direction: column;
}

.review-progress-copy > span,
.review-progress-copy small,
.review-progress-stats span {
  color: $ui-muted;
  font-size: 11px;
}

.review-progress-copy strong {
  color: $ui-text;
  font-size: 16px;
  margin: 2px 0;
}

.review-progress-stats {
  display: flex;
  gap: 28px;
}

.review-progress-stats div {
  align-items: flex-end;
  display: flex;
  flex-direction: column;
}

.review-progress-stats strong {
  color: $ui-text;
  font-size: 20px;
  line-height: 1.1;
}

@media (max-width: 700px) {
  .review-progress-card {
    grid-template-columns: 1fr;
  }

  .review-progress-card .v-progress-linear {
    grid-column: 1;
  }

  .review-progress-stats {
    justify-content: space-between;
  }
}

/* Quebra de linha dentro do toolbar do ui-table-card */
.toolbar-break {
  flex-basis: 100%;
  height: 0;
}

.quick-filter-bar {
  align-items: center;
  border-bottom: 1px solid $ui-border;
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  padding: 12px 16px;
}

.quick-filter-bar > span {
  color: $ui-muted;
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.06em;
  margin-right: 4px;
}

.analysis-toolbar {
  align-items: center;
  border-bottom: 1px solid $ui-border;
  display: flex;
  justify-content: space-between;
  padding: 9px 16px;
}

.selection-block,
.table-controls {
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
}

.selection-block > span {
  color: $ui-muted;
  font-size: 11px;
  margin-right: 8px;
}

/* Filtros compactos: nao esticam ao quebrar linha. */
.ui-toolbar__search.v-input {
  flex: 1 1 200px;
  max-width: 260px;
}

.ui-toolbar__field.v-input {
  flex: 0 1 175px;
  max-width: 190px;
}

.ui-toolbar__field.filter-wide.v-input {
  flex-basis: 225px;
  max-width: 235px;
}

.sort-select {
  max-width: 230px;
  min-width: 210px;
}

.class-chips,
.request-type-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  min-width: 150px;
}

.creditor-cell,
.credit-main {
  display: flex;
  flex-direction: column;
  padding: 7px 0;
}

.credit-company-name {
  cursor: help;
  display: block;
  max-width: 190px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.credit-main small {
  color: $ui-muted;
  font-size: 11px;
}

.credits-wrapper {
  background: $ui-bg-muted;
  /* Do not let the expanded table enlarge its parent colspan cell. */
  box-sizing: border-box;
  width: 0;
  min-width: 100%;
  padding: 12px 16px 18px;
}

.credits-table {
  border: 1px solid $ui-border;
  width: 100%;
  min-width: 0;
}

.credits-table ::v-deep .v-data-table__wrapper {
  overflow-x: auto;
}

.credits-table ::v-deep table {
  min-width: 1450px;
}

.credits-table th:first-child,
.credits-table td:first-child {
  background: $ui-bg;
  left: 0;
  min-width: 260px;
  position: sticky;
  z-index: 1;
}

.pending-card {
  border-color: nth($tone-warning, 3);
}

.batch-letter-title {
  align-items: flex-start;
  display: flex;
  justify-content: space-between;
  padding: 20px 24px;
}

.batch-letter-title span {
  display: block;
  font-size: 18px;
  font-weight: 600;
}

.batch-letter-title small {
  color: $ui-muted;
  display: block;
  font-size: 12px;
  font-weight: 400;
  margin-top: 2px;
}

.batch-letter-body {
  background: $ui-bg-muted;
  padding: 20px 24px !important;
}

.batch-letter-section {
  background: $ui-bg;
  border: 1px solid $ui-border;
  border-radius: 8px;
  margin-bottom: 16px;
  padding: 16px;
}

.batch-letter-section:last-child {
  margin-bottom: 0;
}

.batch-letter-section > header {
  align-items: center;
  display: flex;
  gap: 8px;
  margin-bottom: 12px;
}

.batch-letter-section > header .v-icon {
  color: $ui-muted !important;
}

.batch-letter-section > header span {
  color: $ui-text;
  font-size: 13px;
  font-weight: 600;
}

.batch-letter-section > header em {
  background: $ui-border-soft;
  border-radius: 10px;
  color: $ui-muted;
  font-size: 11px;
  font-style: normal;
  padding: 1px 8px;
}

.batch-letter-hint {
  color: $ui-muted;
  font-size: 12px;
  margin: 8px 0 0;
}

.batch-letter-rows {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.batch-letter-row {
  border: 1px solid $ui-border;
  border-radius: 8px;
  padding: 12px;
  transition: border-color 0.15s ease;
}

.batch-letter-row:hover {
  border-color: $ui-border-strong;
}

.batch-letter-row--new {
  background: nth($tone-purple, 1);
  border-color: nth($tone-purple, 3);
}

.batch-letter-row__head {
  align-items: center;
  display: flex;
  gap: 4px;
  margin-bottom: 10px;
}

/* Credor e CPF ocupam mais espaco; classe, moeda e valor acompanham o
   conteudo que exibem. */
.batch-letter-row__fields {
  display: grid;
  gap: 10px;
  grid-template-columns: minmax(0, 2fr) minmax(0, 1.3fr) minmax(0, 1.6fr) minmax(0, 1fr) minmax(0, 1.2fr);
}

.batch-letter-empty {
  align-items: center;
  border: 1px dashed $ui-border-strong;
  border-radius: 8px;
  color: $ui-muted;
  display: flex;
  flex-direction: column;
  padding: 32px 16px;
  text-align: center;
}

.batch-letter-empty .v-icon {
  color: $ui-subtle !important;
}

.batch-letter-empty p {
  color: $ui-text;
  font-weight: 500;
  margin: 8px 0 2px;
}

.batch-letter-empty span {
  font-size: 12px;
}

.batch-letter-actions {
  padding: 14px 24px;
}

.batch-letter-summary {
  color: $ui-muted;
  font-size: 12px;
}

@media (max-width: 960px) {
  .batch-letter-row__fields {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

.drawer-header small {
  color: $ui-muted;
  font-size: 10px;
  letter-spacing: 0.08em;
}

.drawer-header h2 {
  font-size: 19px;
  margin: 5px 0;
}

.drawer-header span {
  color: $ui-muted;
  font-size: 12px;
}

.drawer-content h3 {
  color: $ui-text;
  font-size: 14px;
}

.comparison-grid {
  display: grid;
  gap: 10px;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  margin-top: 12px;
}

.comparison-grid article {
  border: 1px solid $ui-border;
  border-radius: 6px;
  display: flex;
  flex-direction: column;
  padding: 13px;
}

.comparison-grid span,
.comparison-grid small {
  color: $ui-muted;
  font-size: 10px;
}

.comparison-grid strong {
  font-size: 15px;
  margin: 7px 0;
}

.drawer-tabs {
  border-bottom: 1px solid $ui-border;
}

.drawer-tabs ::v-deep .v-tab {
  font-size: 10px;
  min-width: 0;
  padding: 0 8px;
}

.drawer-reason {
  padding: 8px 12px;
  border: 1px solid $ui-border;
  border-radius: 6px;
  background: $ui-bg-muted;
  font-size: 13px;
  overflow-wrap: anywhere;
}

.drawer-reason--long {
  max-height: 260px;
  overflow: auto;
}

.drawer-reason img {
  max-width: 100%;
  height: auto;
}

.drawer-tab-heading {
  align-items: center;
  display: flex;
  gap: 8px;
  margin: 22px 0 14px;
}

.drawer-tab-heading h3 {
  margin: 0;
}

.drawer-tab-heading small {
  color: $ui-muted;
  display: block;
  font-size: 11px;
  margin-top: 3px;
}

.drawer-decision-form {
  display: grid;
  gap: 4px;
}

.drawer-draft-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  justify-content: flex-end;
  margin-top: 4px;
}

.document-list,
.history-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.document-list article,
.history-list article {
  align-items: center;
  border: 1px solid $ui-border;
  border-radius: 6px;
  display: flex;
  gap: 10px;
  padding: 11px;
}

.document-list article > div,
.history-list article > div {
  display: flex;
  flex: 1;
  flex-direction: column;
}

.document-list strong,
.history-list strong {
  color: $ui-text;
  font-size: 12px;
}

.document-list small,
.history-list small {
  color: $ui-muted;
  font-size: 10px;
}

.history-dot {
  background: $ui-subtle;
  border-radius: 50%;
  height: 8px;
  width: 8px;
}

.history-dot.creditor {
  background: var(--v-primary-base);
}

.history-dot.debtor {
  background: nth($tone-purple, 3);
}

.history-dot.aj {
  background: nth($tone-success, 3);
}

.decision-field {
  min-width: 190px;
}

.decision-result {
  color: nth($tone-success, 2);
  font-weight: 500;
}

.credit-metadata {
  display: grid;
  gap: 0;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  margin-top: 20px;
}

.credit-metadata div {
  border-bottom: 1px solid $ui-border-soft;
  padding: 10px 0;
}

.credit-metadata dt {
  color: $ui-muted;
  font-size: 10px;
  text-transform: uppercase;
}

.credit-metadata dd {
  color: $ui-text;
  font-size: 12px;
  margin: 3px 0 0;
}

.qgc-panel {
  border: 1px solid $ui-border;
  border-radius: 6px;
  display: flex;
  flex-direction: column;
  padding: 18px;
}

.qgc-panel strong {
  font-size: 24px;
  margin: 12px 0 4px;
}

.qgc-panel span,
.qgc-panel small {
  color: $ui-muted;
}

.qgc-evolution {
  display: grid;
  gap: 10px;
  grid-template-columns: repeat(3, minmax(0, 1fr));
}

.qgc-evolution article {
  border: 1px solid $ui-border;
  border-radius: 6px;
  display: flex;
  flex-direction: column;
  min-height: 150px;
  padding: 14px;
}

.qgc-evolution span,
.qgc-evolution small {
  color: $ui-muted;
  font-size: 10px;
}

.qgc-evolution strong {
  color: $ui-text;
  font-size: 17px;
  margin: 10px 0;
}

.qgc-evolution small:last-child {
  margin-top: auto;
  padding-top: 10px;
}

.drawer-actions {
  flex-wrap: wrap;
}

@media (max-width: 900px) {
  .comparison-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .qgc-evolution {
    grid-template-columns: 1fr;
  }

  .analysis-toolbar {
    align-items: flex-start;
    flex-direction: column;
    gap: 10px;
  }
}
</style>